/* global gtag */
import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import './App.css';
import AdMaxBanner from './AdMaxBanner';
import AdMaxBanner_middle from './AdMaxBanner_middle';
import HintButton from './Hint';
import { useTranslation } from 'react-i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import i18n from 'i18next'; 
import { Helmet } from 'react-helmet-async';
import { analyzeFoodImage, analyzeFoodText } from './geminiService';
import { auth, provider, signInWithPopup, signInWithRedirect, signOut, getRedirectResult } from './firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';

// ▼▼▼ 追加: 新しい診断コンポーネントをインポート ▼▼▼
import FortuneDiagnosis from './FortuneDiagnosis';

// ▼▼▼ 言語リソース定義 ▼▼▼
const resources = {
  ja: {
    translation: {
      "greeting": "こんにちは！",
      "metabolism": "代謝",
      "food": "品目",
      "carbs": "糖質",
      "bite": "たべた！",
      "memories": "おもいで",
      "hour": "時間",
      "minute": "分",
      "hint_title": "ヒント",
      "hint": "本アプリは、糖質摂取量の管理をサポートします。 設定する1日の摂取量上限値（g）は「目標体重 × 3」、無理なく過食だけを避けたい場合は「目標体重 × 4」くらいがおすすめです。 気に入っていただけたら、共有アイコンをタップして「ホーム画面に追加」をお願いします♪",
      "daily_decay_help": "設定する1日の摂取量上限値（g）は「目標体重 × 3」、無理なく過食だけを避けたい場合は「目標体重 × 4」くらいがおすすめです。",
      "close": "閉じる",
      "item_sample": "例: パン, 牛乳 300ml",
      "placeholder_typing": "（入力中）",
      "placeholder_loading": "（検索中）",
      "placeholder_example": "例: 30, -100",
      "tweet_text": "「{{item}}（糖: {{grams}}g）」食べたよ。現在の糖質は {{remaining}}g。\n#たべたくん\nhttps://sugar.michitaru.com?from=x",
      "no_item": "（品目なし）",
      "share_on_x": "Xでシェア",
      "daily_decay": "目標糖質摂取量 (g/日)",
      "carbohydrate_query": "{{item}}の糖質量は？",
      "search_carbohydrate": "糖質量の検索",
      "title": "たべたくん",
      "policy": "プライバシーポリシー",
      "site_info": "このサイトについて",
      "link": {
        "privacy": "/privacy.html",
        "about": "/about.html"
      },
      "image": {
        "src": "/toushitsu-tabetakun-kanban-Ver.4.480.png",
        "alt": "たべたくん - 糖質制限したい人を応援するアプリ"
      },
      "update_metrics": "食べたものを記録",
      "live_status": "ライブ",
      "ai_scan": "AIスキャン",
      "target_label": "目標",
      "rate_label": "減少率",
      "share_button": "共有",
      "contact_label": "連絡先",
      "analysis_success": "🍽️ 解析完了！\n\n【料理名】{{name}}\n📏 基準：{{basis}}\n🍞 糖質：{{carbs}}g\n💪 タンパク質：{{protein}}g\n🔥 {{calories}}kcal\n\n💬 アドバイス:\n{{advice}}",
      "analysis_error": "解析に失敗しました。\n通信状況を確認するか、しばらく時間をおいてからもう一度お試しください。",
      "submit_error": "登録に失敗しました",
      "hour_short": "時間",
      "minute_short": "分",
      "rate_unit": "g/時",
      "done": "完了",
      "quick_add_label": "クイック追加 (タップで入力)",
      "submit_button": "たべた",
      "fortune_title": "今日の運勢",
      "fortune_placeholder": "運気を読み込んでいます...",
      "ask_birthdate": "生年月日を設定",
      "save_birthdate": "占う / 更新",
      "cancel": "戻る",
      "fortune_error": "運勢の取得に失敗しました",
      "upload_method": "画像のアップロード方法",
      "take_photo": "カメラで撮影",
      "choose_gallery": "アルバムから選択",
      "cancel_selection": "キャンセル",
      "height": "身長",
      "height_placeholder": "例: 165",
      "height_unit": "cm",
      "std_weight_label": "標準体重",
      "suggest_carb": "推奨糖質",
      "suggest_protein": "推奨タンパク質 (食事摂取基準2025)",
      "suggest_apply": "目安値を適用する",
      "protein": "タンパク質",
      "protein_consumed_label": "今日のタンパク質 (g)",
      "protein_target_label": "目標タンパク質 (g/日)",
      "protein_placeholder": "0",
      "carbs_record_label": "糖質 (g)",
      "protein_record_label": "タンパク質 (g)",
      "protein_target_setting": "目標タンパク質 (g/日)",
      "settings_label": "設定",
      "settings_close": "設定を閉じる"
    }
  },
  en: {
    translation: {
      "greeting": "Hello!",
      "metabolism": "Metabolism",
      "food": "Food",
      "carbs": "Carbs",
      "bite": "Bite!",
      "memories": "Memories",
      "hour": "h",
      "minute": "m",
      "hint_title": "Hint.",
      "hint": "This app helps you manage your carb intake. We recommend setting your daily limit (g) to 'Goal Weight x 3'. If you want to avoid overeating without stress, try 'Goal Weight x 4'. If you like the app, please tap the share icon and select 'Add to Home Screen'♪",
      "daily_decay_help": "Goal Weight x 3'. If you want to avoid overeating without stress, try 'Goal Weight x 4'.",
      "close": "Close.",
      "item_sample": "Ex: Bread, Milk 300ml",
      "placeholder_typing": "(Typing...)",
      "placeholder_loading": "(Searching...)",
      "placeholder_example": "Ex: 30, -100",
      "tweet_text": "I just had '{{item}}' (Carbs: {{grams}}g). Remaining carbs: approx {{remaining}}g. {{decayTimeHours}} left to complete metabolism!\n#PR #lowcarb #carbs\n#MrBite\nhttps://sugar.michitaru.com?from=x",
      "no_item": "(no item)",
      "share_on_x": "Share on X",
      "daily_decay": "daily carbohydrate intake (g/day)",
      "carbohydrate_query": "How much carbohydrate is in {{item}}?",
      "search_carbohydrate": "Search Carbohydrate",
      "title": "Mr. Bite",
      "policy": "Privacy policy.",
      "site_info": "About this site.",
      "link": {
        "privacy": "/privacy_en.html",
        "about": "/about_en.html"
      },
      "image": {
        "src": "/toushitsu-tabetakun-kanban-Ver.4.480.en.png",
        "alt": "Mr. Bite - An app to help you control carbs"
      },
      "update_metrics": "Log What You Ate",
      "live_status": "Live",
      "ai_scan": "AI Scan",
      "target_label": "Target",
      "rate_label": "Rate",
      "share_button": "Share",
      "contact_label": "Contact",
      "analysis_success": "🍽️ Analysis Complete!\n\n[Food] {{name}}\n📏 Basis: {{basis}}\n🍞 Carbs: {{carbs}}g\n💪 Protein: {{protein}}g\n🔥 {{calories}}kcal\n\n💬 Advice:\n{{advice}}",
      "analysis_error": "Analysis failed.\nPlease check your connection or try again later.",
      "submit_error": "Failed to submit.",
      "hour_short": "h",
      "minute_short": "m",
      "rate_unit": "g/h",
      "done": "Done",
      "quick_add_label": "Quick Add (Tap to fill)",
      "submit_button": "Eat",
      "fortune_title": "Today's Fortune",
      "fortune_placeholder": "Reading your stars...",
      "ask_birthdate": "Set Birthdate",
      "save_birthdate": "Update",
      "cancel": "Cancel",
      "fortune_error": "Failed to load fortune",
      "upload_method": "Upload Method",
      "take_photo": "Take Photo",
      "choose_gallery": "Choose from Gallery",
      "cancel_selection": "Cancel",
      "height": "Height",
      "height_placeholder": "e.g. 165",
      "height_unit": "cm",
      "std_weight_label": "Ideal Weight",
      "suggest_carb": "Suggested Carbs",
      "suggest_protein": "Suggested Protein (DRI 2025)",
      "suggest_apply": "Apply suggested values",
      "protein": "Protein",
      "protein_consumed_label": "Today's Protein (g)",
      "protein_target_label": "Daily Protein Target (g)",
      "protein_placeholder": "0",
      "carbs_record_label": "Carbs Log (g)",
      "protein_record_label": "Protein Log (g)",
      "protein_target_setting": "Protein Target (g/day)",
      "settings_label": "Settings",
      "settings_close": "Close Settings"
    }
  }
};

// i18n初期化
if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: 'en',
      detection: {
        order: ['navigator', 'htmlTag', 'path', 'subdomain'],
        caches: [] 
      },
      interpolation: { escapeValue: false }
    });
} else {
  Object.keys(resources).forEach(lang => {
    i18n.addResourceBundle(lang, 'translation', resources[lang].translation, true, true);
  });
}

const OverseasBanner = () => {
  const bannerRef = useRef(null);
  useEffect(() => {
    if (!bannerRef.current || bannerRef.current.firstChild) return;
    window.atOptions = { 'key' : '737ca67a616d07d822868ecc2fe88541', 'format' : 'iframe', 'height' : 250, 'width' : 300, 'params' : {} };
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = "https://www.highperformanceformat.com/737ca67a616d07d822868ecc2fe88541/invoke.js";
    script.async = true;
    bannerRef.current.appendChild(script);
  }, []);
  return <div ref={bannerRef} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto', width: '300px', height: '250px' }} />;
};

const OverseasExtraAd = () => {
  const adRef = useRef(null);
  useEffect(() => {
    if (!adRef.current || adRef.current.querySelector('script')) return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = "https://pl28269733.effectivegatecpm.com/52/a1/d7/52a1d795693722b0e35e962a26ec84fb.js";
    script.async = true;
    adRef.current.appendChild(script);
  }, []);
  return <div ref={adRef} />;
};

const Icons = {
  Refresh: () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 21h5v-5"/></svg>),
  Flame: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.332-.224-4.818 1.5-5 2.031.242 3 2.5 3 4.5 0 2-1 3-1 3a2.5 2.5 0 0 0 2.5 2.5 2.5 2.5 0 0 0 2.5-2.5z"/></svg>),
  Calendar: () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>),
  Activity: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>),
  Bot: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>),
  Twitter: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>),
  User: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>),
  LogOut: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>),
  Check: () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>),
  Camera: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>),
  Search: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>),
  Sparkles: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L12 3Z"/></svg>),
  Trash: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>),
  Edit: () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>),
  Settings: () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.72l-.15.1a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.1a2 2 0 0 1-1-1.72v-.51a2 2 0 0 1 1-1.72l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>),
};

const COLORS = {
  bg: '#f3f4f6', cardBg: '#ffffff', cardBorder: '#e5e7eb', textMain: '#374151', textSub: '#6b7280', textMuted: '#9ca3af', accent: '#2563eb', inputBg: '#ffffff',
};

const StatCard = ({ label, value, subValue, icon: Icon, iconColor }) => (
  <div style={{ 
    backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', height: '100%', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', boxSizing: 'border-box',
    position: 'relative'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', width: '100%', minHeight: '24px' }}>
      <span style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.textSub }}>{label}</span>
      <div style={{ color: iconColor }}>{Icon && <Icon />}</div>
    </div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', textAlign: 'left', paddingBottom: '4px' }}>
      <div style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.textMain, lineHeight: 1.2 }}>{value}</div>
      {subValue && (<div style={{ fontSize: '16px', color: COLORS.textMuted, marginTop: '4px' }}>{subValue}</div>)}
    </div>
  </div>
);

function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000*20); return () => clearInterval(id); }, []);
  return now;
}

function useDeviceId() {
  const key = 'device_id';
  const [id] = useState(() => {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
    
    // crypto.randomUUID がない場合のフォールバック（簡易版UUID生成）
    const uuid = typeof crypto !== 'undefined' && crypto.randomUUID 
      ? crypto.randomUUID() 
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });

    localStorage.setItem(key, uuid);
    return uuid;
  });
  return id;
}

function getColorByRatio(grams, dailyTarget) {
  const target = Math.max(1, Number(dailyTarget || 0));
  const ratio = grams / target;
  if (ratio < 0.85) return '#22c55e';
  if (ratio < 1.15) return '#16a34a';
  if (ratio < 1.45) return '#eab308';
  return '#ef4444';
}

// 身長(cm) → 標準体重・推奨糖質・推奨タンパク質を計算
// タンパク質: 厚生労働省「日本人の食事摂取基準2025年版」に基づく
// 推奨量 = 標準体重(BMI22) × 1.0 g/kg/日
// （推定平均必要量0.66 × 推奨量係数1.25 ≒ 0.83g/kg、参照体重ベースで実質1.0g/kg）
function calcNutritionTargets(heightCm) {
  const h = Number(heightCm);
  if (!h || h < 100 || h > 250) return null;
  const hm = h / 100;
  const stdWeight = Math.round(hm * hm * 22 * 10) / 10;
  return {
    stdWeight,
    carbTarget: Math.round(stdWeight * 3),
    proteinTarget: Math.round(stdWeight * 1.0), // 2025年版食事摂取基準準拠
  };
}

// 今日の日付文字列(JST) を返す（localStorage の日付比較用）
function getTodayLocalStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function DailyDecayHelpPopup() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const [alignUp, setAlignUp] = useState(false);
  const [shiftX, setShiftX] = useState(0);
  const [shiftY, setShiftY] = useState(0);
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      if (triggerRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const pop = popRef.current?.getBoundingClientRect();
      if (!trigger || !pop) return;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const willOverflowRight = trigger.left + pop.width > vw - margin;
      const willOverflowBottom = trigger.bottom + pop.height > vh - margin;
      setAlignRight(willOverflowRight);
      setAlignUp(willOverflowBottom);
      let desiredLeft = willOverflowRight ? trigger.right - pop.width : trigger.left;
      let clampedLeft = Math.max(margin, Math.min(desiredLeft, vw - margin - pop.width));
      setShiftX(clampedLeft - desiredLeft);
      let desiredTop = willOverflowBottom ? trigger.top - pop.height - 8 : trigger.bottom + 8;
      let clampedTop = Math.max(margin, Math.min(desiredTop, vh - margin - pop.height));
      setShiftY(clampedTop - desiredTop);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [open]);

  const popStyle = {
    position: 'absolute', left: alignRight ? 'auto' : 0, right: alignRight ? 0 : 'auto', top: alignUp ? 'auto' : '1.5em', bottom: alignUp ? '1.5em' : 'auto', transform: `translate(${shiftX}px, ${shiftY}px)`, background: '#fff', border: '1px solid #ccc', padding: '0.5em 0.75em', borderRadius: '6px', width: 'min(280px, 80vw)', zIndex: 1000, boxShadow: '0 6px 24px rgba(0,0,0,0.15)', wordBreak: 'break-word', color: '#333'
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span ref={triggerRef} onClick={() => setOpen(v => !v)} style={{ cursor: 'pointer', color: 'blue', marginLeft: 8 }} role="button" aria-expanded={open} tabIndex={0}>？</span>
      {open && <div ref={popRef} style={popStyle}>{t('daily_decay_help')}</div>}
    </span>
  );
}

function GaugeInfoPopup({ isJapan }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const [alignUp, setAlignUp] = useState(false);
  const [shiftX, setShiftX] = useState(0);
  const [shiftY, setShiftY] = useState(0);
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      if (triggerRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const pop = popRef.current?.getBoundingClientRect();
      if (!trigger || !pop) return;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const willOverflowRight = trigger.left + pop.width > vw - margin;
      const willOverflowBottom = trigger.bottom + pop.height > vh - margin;
      setAlignRight(willOverflowRight);
      setAlignUp(willOverflowBottom);
      let desiredLeft = willOverflowRight ? trigger.right - pop.width : trigger.left;
      let clampedLeft = Math.max(margin, Math.min(desiredLeft, vw - margin - pop.width));
      setShiftX(clampedLeft - desiredLeft);
      let desiredTop = willOverflowBottom ? trigger.top - pop.height - 8 : trigger.bottom + 8;
      let clampedTop = Math.max(margin, Math.min(desiredTop, vh - margin - pop.height));
      setShiftY(clampedTop - desiredTop);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [open]);

  const popStyle = {
    position: 'absolute', left: alignRight ? 'auto' : 0, right: alignRight ? 0 : 'auto',
    top: alignUp ? 'auto' : '1.5em', bottom: alignUp ? '1.5em' : 'auto',
    transform: `translate(${shiftX}px, ${shiftY}px)`,
    background: '#fff', border: '1px solid #ccc', padding: '0.6em 0.85em',
    borderRadius: '8px', width: 'min(280px, 80vw)', zIndex: 1000,
    boxShadow: '0 6px 24px rgba(0,0,0,0.15)', wordBreak: 'break-word',
    color: '#444', fontSize: '16px', lineHeight: '1.6'
  };

  const text = isJapan
    ? '数値は過去48時間の摂取量を時間経過とともに減衰させた1日平均の目安です。'
    : 'Values are a daily average estimate based on intake over the past 48 hours, weighted by time elapsed.';

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span ref={triggerRef} onClick={() => setOpen(v => !v)}
        style={{ cursor: 'pointer', color: '#a78bfa', marginLeft: 6, fontSize: '16px', fontWeight: 'bold', userSelect: 'none' }}
        role="button" aria-expanded={open} tabIndex={0}>ⓘ</span>
      {open && (
        <div ref={popRef} style={popStyle}>
          {text.split('\n\n').map((line, i) => <p key={i} style={{ margin: i === 0 ? 0 : '8px 0 0 0' }}>{line}</p>)}
        </div>
      )}
    </span>
  );
}

// ========== 今日の暦カード ==========

const SYNODIC_MONTH = 29.530588853;
const KNOWN_NEW_MOON = new Date(Date.UTC(2000, 0, 6, 18, 14, 0)); // 2000-01-06 18:14 UTC

function getMoonAge(date) {
  const msPerDay = 86400000;
  const elapsed = (date - KNOWN_NEW_MOON) / msPerDay;
  return ((elapsed % SYNODIC_MONTH) + SYNODIC_MONTH) % SYNODIC_MONTH;
}

function getMoonPhaseInfo(moonAge) {
  if (moonAge < 1.85)  return { emoji: '🌑', ja: '新月',     en: 'New Moon' };
  if (moonAge < 7.38)  return { emoji: '🌒', ja: '三日月',   en: 'Waxing Crescent' };
  if (moonAge < 9.22)  return { emoji: '🌓', ja: '上弦の月', en: 'First Quarter' };
  if (moonAge < 13.80) return { emoji: '🌔', ja: '十三夜',   en: 'Waxing Gibbous' };
  if (moonAge < 15.75) return { emoji: '🌕', ja: '満月',     en: 'Full Moon' };
  if (moonAge < 16.73) return { emoji: '🌖', ja: '十六夜',   en: 'Waning Gibbous' };
  if (moonAge < 17.72) return { emoji: '🌖', ja: '立待月',   en: 'Waning Gibbous' };
  if (moonAge < 18.71) return { emoji: '🌖', ja: '居待月',   en: 'Waning Gibbous' };
  if (moonAge < 20.18) return { emoji: '🌖', ja: '寝待月',   en: 'Waning Gibbous' };
  if (moonAge < 21.65) return { emoji: '🌖', ja: '更待月',   en: 'Waning Gibbous' };
  if (moonAge < 23.61) return { emoji: '🌗', ja: '下弦の月', en: 'Last Quarter' };
  if (moonAge < 27.00) return { emoji: '🌘', ja: '有明月',   en: 'Waning Crescent' };
  return                      { emoji: '🌑', ja: '新月間近', en: 'Dark Moon' };
}

function getRokuyo(date) {
  const msPerDay = 86400000;
  const elapsed = (date - KNOWN_NEW_MOON) / msPerDay;
  const monthsSince = Math.floor(elapsed / SYNODIC_MONTH);
  const moonAge = ((elapsed % SYNODIC_MONTH) + SYNODIC_MONTH) % SYNODIC_MONTH;
  // 2000-01-06 = 旧暦1999年12月1日 → refLunarMonth=12
  const lunarMonth = ((12 - 1 + monthsSince) % 12) + 1;
  const lunarDay = Math.floor(moonAge) + 1;
  const names = ['先勝', '友引', '先負', '仏滅', '大安', '赤口'];
  const colors = ['#f59e0b','#10b981','#6b7280','#64748b','#ef4444','#ef4444'];
  const idx = (lunarMonth + lunarDay) % 6;
  return { ja: names[idx], color: colors[idx] };
}

// [month, day, ja, en, yoton(陽遁=true/陰遁=false), startStar(0=一白...8=九紫)]
const SEKKI_DATA = [
  [1,6,'小寒','Minor Cold',           true, 6],
  [1,20,'大寒','Major Cold',          true, 3],
  [2,4,'立春','Start of Spring',      true, 0],
  [2,19,'雨水','Rain Water',          true, 6],
  [3,6,'啓蟄','Awakening of Insects', true, 3],
  [3,21,'春分','Spring Equinox',      true, 0],
  [4,5,'清明','Clear and Bright',     true, 6],
  [4,20,'穀雨','Grain Rain',          true, 3],
  [5,6,'立夏','Start of Summer',      true, 0],
  [5,21,'小満','Grain Buds',          true, 6],
  [6,6,'芒種','Grain in Ear',         true, 3],
  [6,21,'夏至','Summer Solstice',     false,8],
  [7,7,'小暑','Minor Heat',           false,2],
  [7,23,'大暑','Major Heat',          false,5],
  [8,7,'立秋','Start of Autumn',      false,8],
  [8,23,'処暑','End of Heat',         false,2],
  [9,8,'白露','White Dew',            false,5],
  [9,23,'秋分','Autumn Equinox',      false,8],
  [10,8,'寒露','Cold Dew',            false,2],
  [10,23,'霜降',"Frost's Descent",    false,5],
  [11,7,'立冬','Start of Winter',     false,8],
  [11,22,'小雪','Minor Snow',         false,2],
  [12,7,'大雪','Major Snow',          false,5],
  [12,22,'冬至','Winter Solstice',    true, 0],
];

function getSekki(date) {
  const m = date.getMonth() + 1, d = date.getDate();
  const found = SEKKI_DATA.find(([sm, sd]) => sm === m && Math.abs(sd - d) <= 1);
  return found ? { ja: found[2], en: found[3] } : null;
}

const KYUSEI_STARS  = ['一白水星','二黒土星','三碧木星','四緑木星','五黄土星','六白金星','七赤金星','八白土星','九紫火星'];
const KYUSEI_BG     = ['#bfdbfe','#374151','#bbf7d0','#86efac','#fde68a','#f3f4f6','#fecaca','#e5e7eb','#e9d5ff'];
const KYUSEI_TEXT   = ['#1e3a8a','#f9fafb','#14532d','#166534','#78350f','#374151','#991b1b','#374151','#581c87'];

function kyuseiInfo(idx) {
  return { name: KYUSEI_STARS[idx], num: idx+1, bg: KYUSEI_BG[idx], color: KYUSEI_TEXT[idx] };
}

function getYearlyKyusei(date) {
  const y = date.getFullYear(), m = date.getMonth()+1, d = date.getDate();
  const base = (m === 1 || (m === 2 && d <= 3)) ? y - 1 : y;
  // 2026年=一白(0), 毎年1ずつ下降
  return kyuseiInfo(((-(base - 2026)) % 9 + 9) % 9);
}

// 月盤：寅月（立春）の中宮は年盤星で決まる
// 一白,四緑,七赤年 → 寅月=八白(7) / 二黒,五黄,八白年 → 五黄(4) / 三碧,六白,九紫年 → 二黒(1)
const TORA_BASE = [7,4,1,7,4,1,7,4,1]; // 年盤idx→寅月中宮idx
const MONTH_SEKKI = [[2,4],[3,6],[4,5],[5,6],[6,6],[7,7],[8,7],[9,8],[10,8],[11,7],[12,7],[1,6]];
// index: 0=寅(立春)〜11=丑(小寒) ※月ごとに中宮は1ずつ下降

function getMonthlyKyusei(date) {
  const y = date.getFullYear(), dm = date.getMonth()+1, dd = date.getDate();
  const today = new Date(y, dm-1, dd);
  const allMonths = [];
  for (let dy = -1; dy <= 1; dy++) {
    MONTH_SEKKI.forEach(([sm, sd], i) => {
      const yr = sm === 1 ? y + dy + 1 : y + dy;
      allMonths.push({ date: new Date(yr, sm-1, sd), monthIdx: i, baseYear: sm===1 ? yr-1 : yr });
    });
  }
  allMonths.sort((a,b) => a.date - b.date);
  let cur = null;
  for (let i = allMonths.length-1; i>=0; i--) {
    if (allMonths[i].date <= today) { cur = allMonths[i]; break; }
  }
  if (!cur) return null;
  const yearIdx = ((-(cur.baseYear - 2026)) % 9 + 9) % 9;
  const toraBase = TORA_BASE[yearIdx];
  return kyuseiInfo(((toraBase - cur.monthIdx) % 9 + 9) % 9);
}

function getDailyKyusei(date) {

  const y = date.getFullYear();
  const msPerDay = 86400000;
  const today = new Date(y, date.getMonth(), date.getDate());

  // 前後の年も含めた全節入り日リストを生成
  const allSekki = [-1, 0, 1].flatMap(dy =>
    SEKKI_DATA.map(([m, d,,,yoton, startStar]) => ({
      date: new Date(y + dy, m - 1, d),
      yoton, startStar,
    }))
  ).sort((a, b) => a.date - b.date);

  // 今日以前の直近節入りを探す
  let current = null;
  for (let i = allSekki.length - 1; i >= 0; i--) {
    if (allSekki[i].date <= today) { current = allSekki[i]; break; }
  }
  if (!current) return null;

  const days = Math.round((today - current.date) / msPerDay);
  const idx = current.yoton
    ? (current.startStar + days) % 9
    : ((current.startStar - days) % 9 + 9) % 9;

  return kyuseiInfo(idx);
}

function getHoliday(date) {
  const y = date.getFullYear(), m = date.getMonth() + 1, d = date.getDate(), dow = date.getDay();
  const fixed = [
    [1,1,'元日',"New Year's Day"],
    [2,11,'建国記念の日','National Foundation Day'],
    [2,23,'天皇誕生日',"Emperor's Birthday"],
    [4,29,'昭和の日','Showa Day'],
    [5,3,'憲法記念日','Constitution Day'],
    [5,4,'みどりの日','Greenery Day'],
    [5,5,'こどもの日',"Children's Day"],
    [8,11,'山の日','Mountain Day'],
    [11,3,'文化の日','Culture Day'],
    [11,23,'勤労感謝の日','Labour Thanksgiving Day'],
  ];
  for (const [fm,fd,ja,en] of fixed) {
    if (fm === m && fd === d) return { ja, en };
    // 振替休日（日曜→月曜）
    if (dow === 1 && fm === m && fd === d - 1) return { ja: `${ja}（振替休日）`, en: `${en} (Observed)` };
  }
  // ハッピーマンデー
  if (dow === 1) {
    if (m === 1 && d >= 8  && d <= 14) return { ja: '成人の日',   en: 'Coming of Age Day' };
    if (m === 7 && d >= 15 && d <= 21) return { ja: '海の日',     en: 'Marine Day' };
    if (m === 9 && d >= 15 && d <= 21) return { ja: '敬老の日',   en: 'Respect for the Aged Day' };
    if (m === 10&& d >= 8  && d <= 14) return { ja: 'スポーツの日','en': 'Sports Day' };
  }
  // 春分・秋分（近似式）
  if (m === 3) {
    const eq = Math.floor(20.8431 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
    if (d === eq) return { ja: '春分の日', en: 'Vernal Equinox Day' };
    if (dow === 1 && d === eq + 1) return { ja: '春分の日（振替休日）', en: 'Vernal Equinox Day (Observed)' };
  }
  if (m === 9) {
    const eq = Math.floor(23.2488 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
    if (d === eq) return { ja: '秋分の日', en: 'Autumnal Equinox Day' };
    if (dow === 1 && d === eq + 1) return { ja: '秋分の日（振替休日）', en: 'Autumnal Equinox Day (Observed)' };
  }
  return null;
}

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function TodayInfoCard({ isJapan, onFortuneClick }) {
  const now = new Date();
  const moonAge = getMoonAge(now);
  const moon = getMoonPhaseInfo(moonAge);
  const rokuyo = getRokuyo(now);
  const sekki = getSekki(now);
  const holiday = getHoliday(now);
  const weekNum = getISOWeek(now);
  const kyuseiYear  = getYearlyKyusei(now);
  const kyuseiMonth = getMonthlyKyusei(now);
  const kyuseiDay   = getDailyKyusei(now);

  const jaWeekdays = ['日曜','月曜','火曜','水曜','木曜','金曜','土曜'];
  const enWeekdays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const dow = now.getDay();

  const getWareki = (date) => {
    const y = date.getFullYear(), m = date.getMonth() + 1, d = date.getDate();
    if (y > 2019 || (y === 2019 && (m > 5 || (m === 5 && d >= 1))))
      return `令和${y - 2018}年`;
    if (y > 1989 || (y === 1989 && (m > 1 || (m === 1 && d >= 8))))
      return `平成${y - 1988}年`;
    return `${y}年`;
  };

  const wareki = isJapan ? getWareki(now) : null;

  const dateStr = isJapan
    ? `${now.getFullYear()}年 (${wareki}) ${now.getMonth()+1}月${now.getDate()}日 ${jaWeekdays[dow]}`
    : `${enWeekdays[dow]}, ${now.toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}`;

  const cardStyle = {
    backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: '16px',
    padding: '16px 20px', border: '1px solid #e5e7eb',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', textAlign: 'center',
    position: 'relative',
  };
  const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' };
  const badgeStyle = (bg, color) => ({
    backgroundColor: bg, color, borderRadius: '20px',
    padding: '3px 12px', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap',
  });

  return (
    <div style={cardStyle}>
      {/* 占いアイコン */}
      <button onClick={onFortuneClick} title={isJapan ? '今日の運勢を見る' : "Today's fortune"}
        style={{ position: 'absolute', top: '10px', right: '12px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', padding: '4px', lineHeight: 1 }}>
        🔮
      </button>

      {/* 日付 */}
      <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#854d0e' }}>
        🌞 {dateStr}
      </div>

      <div style={rowStyle}>
        {/* 月齢 */}
        <span style={badgeStyle('#f1f5f9','#334155')}>
          {isJapan ? `${moon.emoji}${moon.ja} (月齢${Math.floor(moonAge)}日)` : `${moon.emoji}${moon.en} (Day ${Math.floor(moonAge)})`}
        </span>

        {/* 六曜（日本のみ）*/}
        {isJapan && (
          <span style={badgeStyle(
            rokuyo.ja === '大安' ? '#dcfce7' : rokuyo.ja === '仏滅' ? '#fee2e2' : '#f8fafc',
            rokuyo.ja === '大安' ? '#166534' : rokuyo.ja === '仏滅' ? '#991b1b' : '#475569'
          )}>
            {rokuyo.ja}
          </span>
        )}

        {/* 週番号（英語のみ）*/}
        {!isJapan && (
          <span style={badgeStyle('#f1f5f9','#334155')}>
            Week {weekNum}
          </span>
        )}

        {/* 二十四節気 */}
        {sekki && (
          <span style={badgeStyle('#d1fae5','#065f46')}>
            🌸 {isJapan ? sekki.ja : sekki.en}
          </span>
        )}

        {/* 祝日 */}
        {holiday && (
          <span style={badgeStyle('#fee2e2','#b91c1c')}>
            🎌 {isJapan ? holiday.ja : holiday.en}
          </span>
        )}

      </div>

      {/* 九星気学 年盤・月盤・日盤（日本のみ・1行）*/}
      {isJapan && (
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {[['年', kyuseiYear], ['月', kyuseiMonth], ['日', kyuseiDay]].map(([label, k]) => k && (
            <span key={label} style={{
              display: 'inline-flex', alignItems: 'baseline', gap: '3px',
              backgroundColor: k.bg, color: k.color,
              borderRadius: '20px', padding: '3px 10px',
              fontWeight: 'bold', whiteSpace: 'nowrap',
            }}>
              <span style={{ fontSize: '11px', opacity: 0.65 }}>{label}</span>
              <span style={{ fontSize: '13px' }}>{k.name}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const CarbGauge = ({ current, max, proteinConsumed = 0, proteinTarget = 0, children }) => {
  const target = Math.max(1, Number(max || 0));
  const ratio = target > 0 ? current / target : 0;
  
  const C_BG = '#f3f4f6'; const C_GREEN = '#22c55e'; const C_GREEN_DARK = '#16a34a'; const C_YELLOW = '#eab308'; const C_RED = '#ef4444';
  const C_RED_DARK = '#b91c1c'; const C_PURPLE = '#a855f7'; const C_PURPLE_DARK = '#7e22ce'; const C_GRAY = '#4b5563'; const C_BLACK = '#111827';

  const wholeLap = Math.floor(ratio);
  const isLap1 = wholeLap < 1;
  const isLap2 = wholeLap === 1;
  const isLap3 = wholeLap === 2;
  const isLap4 = wholeLap === 3;
  const isLap5Plus = wholeLap >= 4;
  const lapProgress = isLap5Plus ? Math.min(1, ratio - 4) : ratio - wholeLap;

  let trackColor = C_BG;
  if (isLap2) trackColor = C_GREEN;
  else if (isLap3) trackColor = C_RED;
  else if (isLap4) trackColor = C_PURPLE;
  else if (isLap5Plus) trackColor = C_GRAY;

  // 内側：糖質リング
  const size = 320; const radius = 140; const stroke = 24; const center = size / 2;
  const normalizedRadius = radius - stroke / 2; const circumference = normalizedRadius * 2 * Math.PI;

  // 1周目: 明るい緑(0〜0.85) + 濃い緑(0.85〜ratio)
  const brightGreenLen = isLap1 ? Math.min(lapProgress, 0.85) * circumference : 0;
  const darkGreen1Len  = isLap1 && lapProgress > 0.85 ? (lapProgress - 0.85) * circumference : 0;
  // 2周目: trackの0.85〜1.0に濃い緑オーバーレイ（常に0.15幅） + 2周目の各弧
  const darkOverlayLen = isLap2 ? 0.15 * circumference : 0;
  const darkGreen2Len  = isLap2 ? Math.min(lapProgress, 0.15) * circumference : 0;
  const yellowLen      = isLap2 && lapProgress > 0.15 ? (Math.min(lapProgress, 0.45) - 0.15) * circumference : 0;
  const redLen         = isLap2 && lapProgress > 0.45 ? (lapProgress - 0.45) * circumference : 0;
  // 3周目: 濃い赤、4周目: 濃い紫、5周目以降: 黒（trackは各周色、上に進捗弧を重ねる）
  const lap3Len = isLap3 ? lapProgress * circumference : 0;
  const lap4Len = isLap4 ? lapProgress * circumference : 0;
  const lap5Len = isLap5Plus ? lapProgress * circumference : 0;

  // 外側：プロテインリング（内側が糖質リング外周と同じ径）
  const P_STROKE = 24;
  const P_RADIUS = radius + stroke / 2 + P_STROKE / 2; // 糖質外周にぴったり密着
  const P_CIRCUMFERENCE = P_RADIUS * 2 * Math.PI;
  const proteinRatio = proteinTarget > 0 ? Math.min(1, proteinConsumed / proteinTarget) : 0;
  const proteinDashoffset = P_CIRCUMFERENCE - proteinRatio * P_CIRCUMFERENCE;
  const proteinExcessRatio = proteinTarget > 0 ? Math.min(1, Math.max(0, (proteinConsumed - proteinTarget) / proteinTarget)) : 0;
  const proteinExcessDashoffset = P_CIRCUMFERENCE - proteinExcessRatio * P_CIRCUMFERENCE;
  // 200%超過分（第3ラップ）
  const proteinExcess2Ratio = proteinTarget > 0 ? Math.min(1, Math.max(0, (proteinConsumed - proteinTarget * 2) / proteinTarget)) : 0;
  const proteinExcess2Dashoffset = P_CIRCUMFERENCE - proteinExcess2Ratio * P_CIRCUMFERENCE;
  const showProteinRing = proteinTarget > 0;

  // 先端アイコン用：円弧の先端座標を計算（SVGは-90deg回転済みのため角度は0=右から時計回り）
  const carbTipAngle = lapProgress * 2 * Math.PI;
  const carbTipX = center + normalizedRadius * Math.cos(carbTipAngle);
  const carbTipY = center + normalizedRadius * Math.sin(carbTipAngle);

  // プロテイン：最も外側のラップの先端を使う
  const proteinTipRatio = proteinExcess2Ratio > 0 ? proteinExcess2Ratio : proteinExcessRatio > 0 ? proteinExcessRatio : proteinRatio;
  const proteinTipAngle = proteinTipRatio * 2 * Math.PI;
  const proteinTipX = center + P_RADIUS * Math.cos(proteinTipAngle);
  const proteinTipY = center + P_RADIUS * Math.sin(proteinTipAngle);

  return (
    <div style={{ 
      position: 'relative', width: `${size}px`, height: `${size}px`, margin: '0 auto',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.72)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.55)',
      boxShadow: '0 12px 40px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.85)',
    }}>
      <svg height={size} width={size} style={{ transform: 'rotate(-90deg)', overflow: 'visible', position: 'relative', zIndex: 1 }}>
        <defs>
          <filter id="gauge-shadow" x="-50%" y="-50%" width="200%" height="200%"><feDropShadow dx="3" dy="3" stdDeviation="4" floodColor="#000" floodOpacity="0.2"/></filter>
          <filter id="protein-shadow" x="-50%" y="-50%" width="200%" height="200%"><feDropShadow dx="3" dy="3" stdDeviation="4" floodColor="#f472b6" floodOpacity="0.4"/></filter>
        </defs>
        {/* 糖質track */}
        <circle stroke={trackColor} fill="transparent" strokeWidth={stroke} r={normalizedRadius} cx={center} cy={center} style={{ transition: 'stroke 0.3s ease' }} />
        {/* 1周目: 明るい緑弧(0〜85%) */}
        {brightGreenLen > 0 && (
          <circle stroke={C_GREEN} fill="transparent" strokeWidth={stroke} strokeDasharray={`${brightGreenLen} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: 0, transition: 'stroke-dashoffset 0.5s ease-out' }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* 1周目: 濃い緑弧(85%〜) */}
        {darkGreen1Len > 0 && (
          <circle stroke={C_GREEN_DARK} fill="transparent" strokeWidth={stroke} strokeDasharray={`${darkGreen1Len} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: -(0.85 * circumference), transition: 'stroke-dashoffset 0.5s ease-out' }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* 2周目: trackの85〜100%に濃い緑オーバーレイ */}
        {darkOverlayLen > 0 && (
          <circle stroke={C_GREEN_DARK} fill="transparent" strokeWidth={stroke} strokeDasharray={`${darkOverlayLen} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: -(0.85 * circumference) }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* 2周目: 濃い緑弧(0〜15% = 100〜115%) */}
        {darkGreen2Len > 0 && (
          <circle stroke={C_GREEN_DARK} fill="transparent" strokeWidth={stroke} strokeDasharray={`${darkGreen2Len} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: 0, transition: 'stroke-dashoffset 0.5s ease-out' }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* 黄弧 */}
        {yellowLen > 0 && (
          <circle stroke={C_YELLOW} fill="transparent" strokeWidth={stroke} strokeDasharray={`${yellowLen} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: -(0.15 * circumference), transition: 'stroke-dashoffset 0.5s ease-out' }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* 赤弧 */}
        {redLen > 0 && (
          <circle stroke={C_RED} fill="transparent" strokeWidth={stroke} strokeDasharray={`${redLen} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: -(0.45 * circumference), transition: 'stroke-dashoffset 0.5s ease-out' }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* 3周目: 濃い赤の進捗弧（trackは赤） */}
        {lap3Len > 0 && (
          <circle stroke={C_RED_DARK} fill="transparent" strokeWidth={stroke} strokeDasharray={`${lap3Len} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: 0, transition: 'stroke-dashoffset 0.5s ease-out' }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* 4周目: 濃い紫の進捗弧（trackは紫） */}
        {lap4Len > 0 && (
          <circle stroke={C_PURPLE_DARK} fill="transparent" strokeWidth={stroke} strokeDasharray={`${lap4Len} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: 0, transition: 'stroke-dashoffset 0.5s ease-out' }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* 5周目以降: 黒の進捗弧（trackは濃いグレー） */}
        {lap5Len > 0 && (
          <circle stroke={C_BLACK} fill="transparent" strokeWidth={stroke} strokeDasharray={`${lap5Len} ${circumference}`} strokeLinecap="round" style={{ strokeDashoffset: 0, transition: 'stroke-dashoffset 0.5s ease-out' }} r={normalizedRadius} cx={center} cy={center} />
        )}
        {/* プロテインtrack */}
        {showProteinRing && (
          <circle stroke="#fce7f3" fill="transparent" strokeWidth={P_STROKE} r={P_RADIUS} cx={center} cy={center} />
        )}
        {/* プロテインbar: 達成時グリーン、未達成はホットピンク */}
        {showProteinRing && proteinRatio > 0 && (
          <circle
            stroke={proteinRatio >= 1.0 ? '#34d399' : '#f472b6'}
            fill="transparent"
            strokeWidth={P_STROKE}
            strokeDasharray={`${P_CIRCUMFERENCE} ${P_CIRCUMFERENCE}`}
            strokeLinecap="round"
            style={{ strokeDashoffset: proteinDashoffset, transition: 'stroke-dashoffset 0.6s ease-out, stroke 0.4s ease' }}
            r={P_RADIUS}
            cx={center}
            cy={center}
          />
        )}
        {/* プロテイン余剰bar: 100-200%を濃いグリーンで重ね描き */}
        {showProteinRing && proteinExcessRatio > 0 && (
          <circle
            stroke="#059669"
            fill="transparent"
            strokeWidth={P_STROKE}
            strokeDasharray={`${P_CIRCUMFERENCE} ${P_CIRCUMFERENCE}`}
            strokeLinecap="round"
            style={{ strokeDashoffset: proteinExcessDashoffset, transition: 'stroke-dashoffset 0.6s ease-out' }}
            r={P_RADIUS}
            cx={center}
            cy={center}
          />
        )}
        {/* プロテイン第2余剰bar: 200%超過分をさらに深いグリーンで重ね描き */}
        {showProteinRing && proteinExcess2Ratio > 0 && (
          <circle
            stroke="#14532d"
            fill="transparent"
            strokeWidth={P_STROKE}
            strokeDasharray={`${P_CIRCUMFERENCE} ${P_CIRCUMFERENCE}`}
            strokeLinecap="round"
            style={{ strokeDashoffset: proteinExcess2Dashoffset, transition: 'stroke-dashoffset 0.6s ease-out' }}
            r={P_RADIUS}
            cx={center}
            cy={center}
          />
        )}
        {/* 糖質リング先端アイコン（SVGが-90deg回転しているため+90degで補正） */}
        <text x={carbTipX} y={carbTipY} textAnchor="middle" dominantBaseline="middle" fontSize="16"
          transform={`rotate(90, ${carbTipX}, ${carbTipY})`}>🍞</text>
        {/* プロテインリング先端アイコン */}
        {showProteinRing && (
          <text x={proteinTipX} y={proteinTipY} textAnchor="middle" dominantBaseline="middle" fontSize="18"
            transform={`rotate(90, ${proteinTipX}, ${proteinTipY})`}>💪</text>
        )}
      </svg>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2, pointerEvents: 'none' }}>
        {children}
      </div>
    </div>
  );
};

// ▼▼▼ 修正: 古い DailyFortuneWidget 定義は削除しました ▼▼▼

export default function App() {
  const CARB_PER_DAY = 240;
  const MS_PER_DAY = 86400000;
  const MIN_LOADING_TIME = 800;

  const deviceId = useDeviceId();
  const nowMs = useNow();
  const API = process.env.REACT_APP_API_BASE_URL;

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  const [record, setRecord] = useState({ timestamp: null, remainingGrams: 0, dailyDecay: CARB_PER_DAY });
  const [records, setRecords] = useState([]);
  const [dailyDecayInput, setDailyDecayInput] = useState(CARB_PER_DAY);
  const [itemName, setItemName] = useState('');
  const [carbs, setCarbs] = useState('');
  
  const [isRecordsLoading, setIsRecordsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [isFortuneLoading, setIsFortuneLoading] = useState(true);
  const MAX_RETRIES = 3;

  // 占いデータ用 state
  const [fortuneData, setFortuneData] = useState({ fortune: '', birthDate: '', requireBirthDate: false });
  const [fortuneFetchedDate, setFortuneFetchedDate] = useState(getTodayLocalStr());
  const [showFortunePopup, setShowFortunePopup] = useState(false);
  const gaugeCardRef = useRef(null);
  const [gaugeScale, setGaugeScale] = useState(1);

  const { t, i18n } = useTranslation();
  const isJapan = (i18n.language || 'ja').startsWith('ja');

  const [formKey, setFormKey] = useState(0);
  const [analyzingMode, setAnalyzingMode] = useState(null);
  
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const swipeTouchStartX = useRef(null);
  const swipeTouchStartY = useRef(null);

  // ▼▼▼ Step1追加: 身長・プロテイン管理 State ▼▼▼
  const [heightInput, setHeightInput] = useState(
    () => localStorage.getItem('height_cm') || ''
  );
  const [proteinTarget, setProteinTarget] = useState(
    () => Number(localStorage.getItem('protein_target') || 60)
  );
  // プロテイン摂取量: 0-48hで線形減衰（100%→0%）、48h以降カウント外
  const proteinConsumed = useMemo(() => {
    const MS_48H = 48 * 60 * 60 * 1000;
    const total = records
      .filter(r => r.proteinGrams != null && r.proteinGrams !== 0)
      .reduce((sum, r) => {
        const age = nowMs - r.timestamp;
        if (age >= MS_48H) return sum;
        return sum + r.proteinGrams * (1 - age / MS_48H);
      }, 0);
    return Math.round(Math.max(0, total) * 10) / 10;
  }, [records, nowMs]);
  const [proteinInput, setProteinInput] = useState('');
  const [showHeightSuggest, setShowHeightSuggest] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // ▲▲▲ Step1追加ここまで ▲▲▲

  // 追加モーダル用 state
  const [addDatetime, setAddDatetime] = useState('');

  // 28日平均（円グラフ下に常時表示）用 state
  const [statsData, setStatsData] = useState(null); // { carbAvg, proteinAvg, recordCount }
  const [isStatsLoading, setIsStatsLoading] = useState(false);

  // 編集モーダル用 state
  const [editingRecord, setEditingRecord] = useState(null); // { timestamp, item, addedGrams, proteinGrams }
  const [swipedRecordId, setSwipedRecordId] = useState(null);
  const [editItem, setEditItem] = useState('');
  const [editCarbs, setEditCarbs] = useState('');
  const [editProtein, setEditProtein] = useState('');
  const [editDatetime, setEditDatetime] = useState(''); // datetime-local 形式
  const [editAnalyzingMode, setEditAnalyzingMode] = useState(null);

  // PWAインストール促進 state
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null); // Android用
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  
  const fmt1 = (n) => (Number.isFinite(n) ? (Math.round(n * 10) / 10).toFixed(0) : '0');
  const fmt1d = (n) => (Number.isFinite(n) ? (Math.round(n * 10) / 10).toFixed(1) : '0.0');

  const getTodayStr = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = ("0" + (d.getMonth() + 1)).slice(-2);
    const day = ("0" + d.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  };

  // ▼▼▼ 修正: データオブジェクトを受け取れるように拡張 ▼▼▼
  const fetchFortune = useCallback(async (dataInput = null) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      let currentToken = null;
      if (user) {
         currentToken = await user.getIdToken(); 
         headers['Authorization'] = `Bearer ${currentToken}`;
      }
      
      // 文字列なら従来通り、オブジェクトなら分解して送信
      let birthDateToUse = null;
      let estimatedHour = null;
      let diagnosisLog = null;

      if (typeof dataInput === 'string') {
        birthDateToUse = dataInput;
      } else if (typeof dataInput === 'object' && dataInput !== null) {
        birthDateToUse = dataInput.birthDate;
        estimatedHour = dataInput.estimatedHour;
        diagnosisLog = dataInput.diagnosisLog;
      }

      const body = {
        date: getTodayStr(),
        lang: i18n.language === 'ja' ? 'ja' : 'en', 
        deviceId: currentToken ? undefined : deviceId,
        birthDate: birthDateToUse,
        estimatedHour: estimatedHour, // 追加: 推定時間
        diagnosisLog: diagnosisLog    // 追加: 診断ログ
      };

      const res = await fetch(`${API}/fortune`, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();

      setFortuneData(prev => ({
        fortune: data.fortune || '',
        birthDate: data.birthDate || prev.birthDate,  // エラー時も既存の birthDate を保持
        requireBirthDate: !!data.requireBirthDate
      }));
    } catch (e) {
      console.error("Fortune Fetch Error:", e);
    }
  }, [API, deviceId, user, i18n.language]);

  // ゲージカードの幅を監視してスケールを計算
  useEffect(() => {
    if (!gaugeCardRef.current) return;
    const GAUGE_SIZE = 320;
    const observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      setGaugeScale(Math.min(1, (width - 40) / GAUGE_SIZE));
    });
    observer.observe(gaugeCardRef.current);
    return () => observer.disconnect();
  }, []);

  // PWAインストール促進
  useEffect(() => {
    // すでにインストール済み（スタンドアロンモード）なら何もしない
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    // 一度「閉じる」を押したユーザーには再表示しない（7日間）
    const dismissed = localStorage.getItem('installBannerDismissed');
    if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Android Chrome: beforeinstallprompt をキャッチ
    const handler = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS全ブラウザ（Safari/Chrome/Firefox/Edge）: スタンドアロンでなければバナーを表示
    // iOSはどのブラウザもbeforeinstallpromptが発火しないため手順案内を出す
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isIos) {
      // 少し遅延してから表示（ページ読み込み後）
      const timer = setTimeout(() => setShowInstallBanner(true), 3000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // 0時をまたいだら占いを自動更新（20秒ごとのnowMsチェック）
  useEffect(() => {
    const today = getTodayLocalStr();
    if (today !== fortuneFetchedDate) {
      setFortuneFetchedDate(today);
      fetchFortune();
    }
  }, [nowMs]);

  // リダイレクトログイン後の結果を処理（iOSなどredirect方式を使った場合）
  useEffect(() => {
    getRedirectResult(auth).catch(e => {
      if (e.code !== 'auth/popup-closed-by-user') {
        console.error("getRedirectResult error:", e);
      }
    });
  }, []);

  useEffect(() => {
    // onAuthStateChanged自体が遅延した場合の安全タイムアウト（10秒）
    const safetyTimer = setTimeout(() => {
      console.warn("Auth check timed out, proceeding as guest");
      setIsAuthChecking(false);
    }, 10000);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          try {
            // getIdToken にタイムアウト（8秒）: iOS Chrome復帰時にネットワーク待ちでハングする対策
            const idToken = await Promise.race([
              currentUser.getIdToken(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('getIdToken timeout')), 8000))
            ]);
            const guestId = localStorage.getItem('device_id');
            if (guestId) {
              // migrate は fire-and-forget（await しない）: ハングしてもAuthCheckingをブロックしない
              fetch(`${API}/migrate`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ guestDeviceId: guestId })
              }).then(() => localStorage.removeItem('device_id')).catch(e => console.error("Migration failed:", e));
            }
            setUser(currentUser);
            setToken(idToken);
          } catch (tokenErr) {
            console.error("Auth init fetch failed, but keeping user session for retry:", tokenErr);
            setUser(currentUser);
            setToken(null);
          }
        } else {
          setUser(null);
          setToken(null);
        }
      } catch (e) {
        console.error("Auth init fatal error:", e);
      } finally {
        clearTimeout(safetyTimer);
        setIsAuthChecking(false);
      }
    });
    return () => { unsubscribe(); clearTimeout(safetyTimer); };
  }, [API]);

  useEffect(() => {
    const initFromUrl = async () => {
      const params = new URLSearchParams(window.location.search);
      const query = params.get('q');
      if (query) {
        setAnalyzingMode('text'); 
        try {
          const currentLang = i18n.language || 'ja';
          const result = await analyzeFoodText(query, currentLang);
          if (result) {
            setItemName(result.name || query);
            setCarbs(result.carbs != null ? String(result.carbs) : '0');
            alert(t('analysis_success', { name: result.name, basis: result.basis ?? '-', carbs: result.carbs, protein: result.protein ?? 0, calories: result.calories, advice: result.advice }));
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          } else {
            throw new Error(t('analysis_error'));
          }
        } catch (err) { 
          console.error(err); 
          alert(t('analysis_error'));
        } finally { setAnalyzingMode(null); }
      }
    };
    initFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
  // iOS PWAスタンドアロンモード検出（ホーム画面から起動した場合）
  const isStandalone = window.navigator.standalone === true;
  const isInAppBrowser = /Line|FBAN|FBAV|Instagram|Twitter|MicroMessenger|TikTok|Snapchat/i.test(navigator.userAgent || '');
  const handleLogin = async () => {
    try {
      if (isMobile && !isStandalone) {
        // モバイルブラウザ: リダイレクト方式（ポップアップが動作しない）
        await signInWithRedirect(auth, provider);
      } else {
        // デスクトップ or iOS PWAスタンドアロン: ポップアップ方式
        // （PWAでのredirectはSafariに逃げて認証結果がPWAに戻らないためpopupを使う）
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user') {
        alert(`Login failed:\n${error.message}`);
      }
    }
  };
  const handleLogout = async () => { try { await signOut(auth); } catch (error) { console.error("Logout failed", error); } };

  // getIdToken にタイムアウトを付けるヘルパー（iOS Chrome復帰時のハング対策）
  const getIdTokenSafe = useCallback(async () => {
    if (!user) return null;
    return Promise.race([
      user.getIdToken(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('getIdToken timeout')), 5000))
    ]);
  }, [user]);

  const fetchRecord = useCallback(async () => {
    try {
      const headers = {};
      let currentToken = null;
      if (user) {
        currentToken = await getIdTokenSafe();
        if (currentToken) headers['Authorization'] = `Bearer ${currentToken}`;
      }
      const query = currentToken ? '?limit=1' : `?deviceId=${encodeURIComponent(deviceId)}&limit=1`;
      const res = await fetch(`${API}/current${query}`, { headers });
      if (!res.ok) throw new Error(res.statusText);
      const { items } = await res.json();
      if (items.length > 0) {
        const rec = items[0];
        setRecord({ timestamp: Number(rec.timestamp), remainingGrams: Number(rec.remainingGrams), dailyDecay: Number(rec.dailyDecay) });
        setDailyDecayInput(Number(rec.dailyDecay || CARB_PER_DAY));
        if (rec.proteinTarget) setProteinTarget(Number(rec.proteinTarget));
        if (rec.heightCm) setHeightInput(String(Number(rec.heightCm)));
      } else {
        setRecord({ timestamp: null, remainingGrams: 0, dailyDecay: CARB_PER_DAY });
      }
    } catch (e) {
      console.error('最新レコード取得失敗:', e); 
      throw e; 
    }
  }, [API, deviceId, user, CARB_PER_DAY, getIdTokenSafe]);

  const fetchHistory = useCallback(async () => {
    try {
      const headers = {};
      let currentToken = null;
      if (user) {
        currentToken = await getIdTokenSafe();
        if (currentToken) headers['Authorization'] = `Bearer ${currentToken}`;
      }
      const query = currentToken ? '?limit=32' : `?deviceId=${encodeURIComponent(deviceId)}&limit=32`;
      const res = await fetch(`${API}/current${query}`, { headers });
      if (!res.ok) throw new Error(res.statusText);
      const { items } = await res.json();
      if (items.length > 0) {
        setRecords(items.map(i => ({
          timestamp: Number(i.timestamp),
          addedGrams: Number(i.addedGrams),
          item: i.item || '',
          proteinGrams: i.proteinGrams != null ? Number(i.proteinGrams) : null
        })));

        // ▼▼▼ ローリング24時間プロテイン集計 ▼▼▼
        // ▲▲▲ proteinConsumedはuseMemoで自動計算のため集計不要 ▲▲▲
      } else {
        setRecords([]);
      }
    } catch (e) {
      console.error('履歴取得失敗:', e); 
      throw e;
    } 
  }, [API, deviceId, user, getIdTokenSafe]);

  // bfcache（iOS Chrome等）検出 + 長時間バックグラウンド後の強制リロード
  useEffect(() => {
    // bfcacheからの復帰
    const handlePageShow = (e) => {
      if (e.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    // visibilitychange で隠れた時刻を記録し、30分以上なら強制リロード
    let hiddenAt = null;
    const RELOAD_THRESHOLD = 30 * 60 * 1000; // 30分
    const handleVisibilityForReload = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt !== null) {
        if (Date.now() - hiddenAt > RELOAD_THRESHOLD) {
          window.location.reload();
          return;
        }
        hiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityForReload);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityForReload);
    };
  }, []);

  // フォアグラウンド復帰時に自動再フェッチ（fetchRecord/fetchHistory宣言後に配置）
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !isRecordsLoading && !isAuthChecking) {
        fetchRecord();
        fetchHistory();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isRecordsLoading, isAuthChecking, fetchRecord, fetchHistory]);

  const deleteRecord = useCallback(async (timestamp) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      let currentToken = null;
      if (user) {
        currentToken = await user.getIdToken();
        headers['Authorization'] = `Bearer ${currentToken}`;
      }
      const body = currentToken ? { timestamp } : { timestamp, deviceId };
      await fetch(`${API}/log`, { method: 'DELETE', headers, body: JSON.stringify(body) });
      await Promise.all([fetchRecord(), fetchHistory()]);
    } catch (e) {
      console.error('削除失敗:', e);
    }
  }, [API, user, deviceId, fetchRecord, fetchHistory]);

  // 28日平均を取得（円グラフ下に常時表示）
  const fetchStats28d = useCallback(async () => {
    setIsStatsLoading(true);
    setStatsData(null);
    try {
      const DAYS = 28;
      const since = Date.now() - DAYS * 24 * 60 * 60 * 1000;
      const headers = {};
      if (user) {
        const tok = await getIdTokenSafe();
        if (tok) headers['Authorization'] = `Bearer ${tok}`;
      }
      const query = user
        ? `?since=${since}&limit=2000`
        : `?deviceId=${encodeURIComponent(deviceId)}&since=${since}&limit=2000`;
      const res = await fetch(`${API}/current${query}`, { headers });
      if (!res.ok) throw new Error(res.statusText);
      const { items } = await res.json();
      const carbTotal = items.reduce((s, r) => s + (r.addedGrams || 0), 0);
      const proteinTotal = items.reduce((s, r) => s + (r.proteinGrams || 0), 0);
      setStatsData({
        carbAvg: Math.round(carbTotal / DAYS * 10) / 10,
        proteinAvg: Math.round(proteinTotal / DAYS * 10) / 10,
        recordCount: items.length,
        since,
      });
    } catch (e) {
      console.error('統計取得失敗:', e);
      setStatsData({ error: true });
    } finally {
      setIsStatsLoading(false);
    }
  }, [API, user, deviceId, getIdTokenSafe]);

  useEffect(() => {
    if (!API || isAuthChecking) return;
    fetchStats28d();
  }, [API, isAuthChecking, fetchStats28d]);

  // 編集モーダルを開く
  const openEditModal = useCallback((r) => {
    setEditingRecord(r);
    setEditItem(r.item || '');
    setEditCarbs(r.addedGrams != null ? String(r.addedGrams) : '0');
    setEditProtein(r.proteinGrams != null ? String(r.proteinGrams) : '0');
    // timestamp → datetime-local 形式 (YYYY-MM-DDTHH:mm)
    const d = new Date(r.timestamp);
    const pad = n => String(n).padStart(2, '0');
    const local = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setEditDatetime(local);
  }, []);

  // 編集保存
  const handleEditSave = useCallback(async () => {
    if (!editingRecord) return;
    try {
      const headers = { 'Content-Type': 'application/json' };
      let currentToken = null;
      if (user) {
        currentToken = await user.getIdToken();
        headers['Authorization'] = `Bearer ${currentToken}`;
      }
      // datetime-local → UTC timestamp(ms)
      const newTs = editDatetime ? new Date(editDatetime).getTime() : editingRecord.timestamp;
      const body = {
        oldTimestamp: editingRecord.timestamp,
        newTimestamp: newTs,
        item: editItem.trim() || undefined,
        addedGrams: parseFloat(editCarbs) || 0,
        proteinGrams: parseFloat(editProtein) || 0,
        ...(currentToken ? {} : { deviceId }),
      };
      const res = await fetch(`${API}/log`, { method: 'PUT', headers, body: JSON.stringify(body) });
      if (!res.ok) {
        if (res.status === 409) throw new Error('CONFLICT');
        let errDetail = `HTTP ${res.status}`;
        try { const j = await res.json(); errDetail += `: ${j.error || JSON.stringify(j)}`; } catch {}
        throw new Error(errDetail);
      }
      setEditingRecord(null);
      await Promise.all([fetchRecord(), fetchHistory()]);
    } catch (e) {
      console.error('編集失敗:', e);
      if (e.message === 'CONFLICT') {
        alert(isJapan ? '指定した時間に別の記録が集中しています。時間を少しずらしてください。' : 'Too many records at that time. Please choose a slightly different time.');
      } else {
        alert((isJapan ? '編集の保存に失敗しました' : 'Failed to save changes.') + `\n\n[${e.message}]`);
      }
    }
  }, [API, user, deviceId, editingRecord, editDatetime, editItem, editCarbs, editProtein, fetchRecord, fetchHistory]);

  // 編集モーダルからの削除
  const handleEditDelete = useCallback(async () => {
    if (!editingRecord) return;
    if (!window.confirm(isJapan ? 'このレコードを削除しますか？' : 'Delete this record?')) return;
    await deleteRecord(editingRecord.timestamp);
    setEditingRecord(null);
  }, [editingRecord, deleteRecord, isJapan]);

  // 編集モーダル内AI検索
  const handleEditAIScan = useCallback(async () => {
    if (!editItem.trim()) return;
    setEditAnalyzingMode('text');
    try {
      const currentLang = i18n.language || 'ja';
      const result = await analyzeFoodText(editItem, currentLang);
      if (result) {
        setEditCarbs(result.carbs != null ? String(result.carbs) : '0');
        setEditProtein(result.protein != null ? String(result.protein) : '0');
        alert(t('analysis_success', { name: result.name, basis: result.basis ?? '-', carbs: result.carbs, protein: result.protein ?? 0, calories: result.calories, advice: result.advice }));
      } else {
        throw new Error(t('analysis_error'));
      }
    } catch (err) {
      console.error(err);
      alert(t('analysis_error'));
    } finally {
      setEditAnalyzingMode(null);
    }
  }, [editItem, i18n, t]);

  useEffect(() => {
    if (!API || isAuthChecking) return;

    setIsRecordsLoading(true);
    setIsError(false);
    setRetryCount(0);

    const withTimeout = (promise, ms) => Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error(`Timeout ${ms}ms`)), ms))
    ]);
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // 占いは独立して非同期ロード（レコードを待たない）
    setIsFortuneLoading(true);
    fetchFortune().finally(() => setIsFortuneLoading(false));

    const tryInit = async () => {
      // PWA起動直後などでオフラインの場合、最大10秒間onlineになるのを待つ
      if (!navigator.onLine) {
        await Promise.race([
          new Promise(resolve => window.addEventListener('online', resolve, { once: true })),
          new Promise(resolve => setTimeout(resolve, 10000)),
        ]);
      }
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          setRetryCount(attempt + 1);
          await Promise.all([
            withTimeout(fetchRecord(), 12000),
            withTimeout(fetchHistory(), 12000),
          ]);
          setIsRecordsLoading(false);
          return;
        } catch (e) {
          console.error(`初期化エラー (${attempt + 1}/${MAX_RETRIES}):`, e);
          if (attempt < MAX_RETRIES - 1) {
            await sleep(1000 * Math.pow(2, attempt));
          } else {
            setIsError(true);
            setIsRecordsLoading(false);
          }
        }
      }
    };
    tryInit();
  }, [API, deviceId, token, fetchRecord, fetchHistory, isAuthChecking, MAX_RETRIES]);

  // 糖質: 各レコードのaddedGramsを48h線形減衰で合算（タンパク質と同じモデル）
  const currentRemaining = useMemo(() => {
    const MS_48H = 48 * 60 * 60 * 1000;
    const total = records
      .filter(r => r.addedGrams != null)
      .reduce((sum, r) => {
        const age = nowMs - r.timestamp;
        if (age >= MS_48H) return sum;
        return sum + r.addedGrams * (1 - age / MS_48H);
      }, 0);
    return Math.max(0, total);
  }, [records, nowMs]);

  const handleReAdd = useCallback(async (r) => {
    setSwipedRecordId(null);
    setIsRecordsLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      let currentToken = null;
      if (user) {
        currentToken = await user.getIdToken(true);
        headers['Authorization'] = `Bearer ${currentToken}`;
      }
      const added = Number(r.addedGrams) || 0;
      const body = {
        addedGrams: added,
        item: r.item || undefined,
        remainingGrams: currentRemaining + added,
        dailyDecay: dailyDecayInput,
        proteinTarget: proteinTarget,
        deviceId: currentToken ? undefined : deviceId,
        proteinGrams: Number(r.proteinGrams) || 0,
        timestamp: Date.now(),
      };
      const res = await fetch(`${API}/log`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      await Promise.all([fetchRecord(), fetchHistory()]);
    } catch (e) {
      console.error('再登録失敗:', e);
      alert((isJapan ? '再登録に失敗しました' : 'Re-add failed') + `\n${e.message}`);
    } finally {
      setIsRecordsLoading(false);
    }
  }, [user, currentRemaining, dailyDecayInput, proteinTarget, deviceId, API, fetchRecord, fetchHistory, isJapan]);

  const tweetText = t('tweet_text', {
    item: records[0]?.item || t('no_item'),
    grams: records[0]?.addedGrams != null ? fmt1(records[0].addedGrams) : '?',
    remaining: fmt1(currentRemaining ?? 0),
  });

  const dailyTarget = useMemo(() => Number(dailyDecayInput || CARB_PER_DAY), [dailyDecayInput, CARB_PER_DAY]);
  const remainingColor = getColorByRatio(currentRemaining, dailyTarget);

  const bigNumberStyle = useMemo(() => ({
    fontSize: '64px', fontWeight: 'bold', color: remainingColor, marginTop: '24px', lineHeight: '1.0', paddingTop: '0', textShadow: '2px 2px 0px rgba(0,0,0,0.1)'
  }), [remainingColor]);

  const currentLocale = i18n.language || 'ja-JP';

  const handleImageUpload = async (e) => {
    setShowSourceMenu(false);
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzingMode('image'); 
    try {
      const currentLang = i18n.language || 'ja';
      const result = await analyzeFoodImage(file, currentLang);
      if (result) {
        setItemName(result.name || '');
        setCarbs(result.carbs != null ? String(result.carbs) : '0');
        setProteinInput(result.protein != null ? String(result.protein) : '0');
        alert(t('analysis_success', { name: result.name, basis: result.basis ?? '-', carbs: result.carbs, protein: result.protein ?? 0, calories: result.calories, advice: result.advice }));
      } else {
        throw new Error(t('analysis_error'));
      }
    } catch (err) { 
      console.error(err); 
      alert(t('analysis_error')); 
    } finally { 
      setAnalyzingMode(null);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleTextSearch = async () => {
    setAnalyzingMode('text'); 
    try {
      const currentLang = i18n.language || 'ja';
      const result = await analyzeFoodText(itemName, currentLang);
      if (result) {
        setItemName(itemName); // 元の入力を保持（重量情報などを残す）
        setCarbs(result.carbs != null ? String(result.carbs) : '0');
        setProteinInput(result.protein != null ? String(result.protein) : '0');
        alert(t('analysis_success', { name: result.name, basis: result.basis ?? '-', carbs: result.carbs, protein: result.protein ?? 0, calories: result.calories, advice: result.advice }));
      } else {
        throw new Error(t('analysis_error'));
      }
    } catch (err) { 
      console.error(err); 
      alert(t('analysis_error')); 
    } finally { setAnalyzingMode(null); }
  };

  const handleAIScan = () => {
    if (itemName && itemName.trim()) { 
      handleTextSearch(); 
    } else { 
      setShowSourceMenu(true);
    }
  };

  const handleAdd = async () => {
    const added = parseFloat(carbs) || 0;

    if (typeof gtag === 'function') {
      gtag('event', 'carb_logged', { item_name: itemName, grams: added, method: 'click_button' });
      gtag('event', 'conversion', { 'send_to': 'AW-17489994120/LbwmCIvRkIobEIjD8JNB', 'value': 1.0, 'currency': 'JPY' });
    }

    setIsRecordsLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      let currentToken = null;
      if (user) {
        currentToken = await user.getIdToken(true);
        headers['Authorization'] = `Bearer ${currentToken}`;
      }
      const newRemaining = currentRemaining + added;
      const addedProtein = parseFloat(proteinInput);
      const body = {
        addedGrams: added,
        item: itemName.trim() || undefined,
        remainingGrams: newRemaining,
        dailyDecay: dailyDecayInput,
        proteinTarget: proteinTarget,
        heightCm: heightInput ? Number(heightInput) : undefined,
        deviceId: currentToken ? undefined : deviceId,
        proteinGrams: !isNaN(addedProtein) ? addedProtein : 0,
        timestamp: Date.now(),
      };
      const res = await fetch(`${API}/log`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(`Log POST Error: ${res.status} ${res.statusText}`);

      setItemName('');
      setCarbs('');
      setProteinInput('');
      setFormKey(k => k + 1);
      await Promise.all([fetchRecord(), fetchHistory()]);
    } catch (e) {
      console.error('登録または更新失敗:', e);
      alert(t('submit_error') + `\n(Error: ${e.message})`);
    } finally {
      setIsRecordsLoading(false);
    }
  };

  const handleItemNameChange = (val) => { setItemName(val); };

  const menuStyles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '20px'
    },
    container: {
      backgroundColor: '#fff', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '350px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: '12px'
    },
    title: { textAlign: 'center', fontWeight: 'bold', marginBottom: '8px', color: COLORS.textSub },
    button: {
      padding: '12px', fontSize: '16px', borderRadius: '8px', border: 'none', backgroundColor: '#f3f4f6', color: COLORS.textMain, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 'bold'
    },
    cancelButton: {
      padding: '12px', fontSize: '16px', borderRadius: '8px', border: `1px solid ${COLORS.cardBorder}`, backgroundColor: '#fff', color: COLORS.textSub, cursor: 'pointer', marginTop: '8px'
    }
  };

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: COLORS.bg, backgroundImage: 'url("/ita-bg.webp")',
      backgroundSize: 'cover', backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
      color: COLORS.textMain, fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      position: 'relative', overflowX: 'hidden'
    }}>
      <Helmet><title>{t('title')}</title></Helmet>

      {isError && (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            background: '#fef2f2', borderBottom: '1px solid #fca5a5',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '12px', padding: '10px 16px', color: '#991b1b'
        }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{isJapan ? '通信エラー：データを取得できませんでした' : 'Communication error: Could not load data'}</span>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '4px 14px', fontSize: '16px', fontWeight: 'bold',
                backgroundColor: '#dc2626', color: '#fff',
                border: 'none', borderRadius: '16px', cursor: 'pointer'
              }}
            >
              {isJapan ? '再読み込み' : 'Retry'}
            </button>
        </div>
      )}
      <div style={{ maxWidth: '420px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
        
        <div style={{ padding: '16px 24px 0 24px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
          {user ? (
             <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.8)', padding: '4px 8px', borderRadius: '20px', fontSize: '16px' }}>
                <img src={user.photoURL} alt="user" style={{ width: 20, height: 20, borderRadius: '50%' }} />
                <span>{user.displayName}</span>
                <button onClick={handleLogout} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: COLORS.textSub }} title="Logout"><Icons.LogOut /></button>
             </div>
          ) : (
             <button onClick={isInAppBrowser ? () => alert(isJapan ? 'LINEなどのアプリ内ブラウザではGoogleログインが利用できません。\nSafariまたはChromeで開いてください。' : 'Google login is not available in in-app browsers (e.g. LINE).\nPlease open in Safari or Chrome.') : handleLogin} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px', fontWeight: 'bold', background: '#fff', border: `1px solid ${COLORS.cardBorder}`, padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', color: COLORS.textMain }}>
               <Icons.User /> Login / Signup
             </button>
          )}
          <HintButton />
        </div>

        {isInAppBrowser && !user && (
          <div style={{ margin: '0 16px 16px', backgroundColor: 'rgba(254,243,199,0.95)', borderRadius: '12px', padding: '12px 16px', border: '1px solid #fcd34d', textAlign: 'center', fontSize: '16px', color: '#92400e', fontWeight: 'bold', lineHeight: 1.6 }}>
            {isJapan
              ? '⚠️ アプリ内ブラウザではGoogleログインが使用できません。\nSafari / Chrome で開いてご利用ください。'
              : '⚠️ Google login is unavailable in in-app browsers.\nPlease open in Safari or Chrome.'}
          </div>
        )}
        <div style={{ textAlign: 'center', marginBottom: '24px', padding: '10px 16px 0 16px' }}>
          <a href="https://sugar.michitaru.com" target="_blank" rel="noopener noreferrer">
            <img src={t('image.src')} alt={t('image.alt')} style={{ width: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }} />
          </a>
        </div>

        <div style={{ padding: '0 24px 24px 24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

          <TodayInfoCard isJapan={isJapan} onFortuneClick={() => setShowFortunePopup(true)} />


          <div ref={gaugeCardRef} style={{ backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: '16px', padding: '14px 20px 36px 20px', border: `1px solid ${COLORS.cardBorder}`, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center', position: 'relative' }}>
              {isRecordsLoading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1, fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold', gap: '8px' }}>
                  {isJapan ? '🔄 読み込み中...' : '🔄 Loading...'}
                  <span style={{ fontSize: '16px', color: '#aaa', fontWeight: 'normal' }}>
                    auth:{String(isAuthChecking)} rec:{String(isRecordsLoading)}
                  </span>
                </div>
              )}
              <div style={{ marginBottom: '40px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px' }}>
                  <GaugeInfoPopup isJapan={isJapan} />
                </div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#854d0e', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <span>📊</span> {isJapan ? '糖質・タンパク質（1日平均）' : 'Carbs & Protein (Daily Avg.)'}
                </div>
              </div>
              <div style={{ position: 'relative', height: `${320 * gaugeScale + 16}px` }}>
              <div style={{ position: 'absolute', top: 0, left: '50%', width: '320px', transform: `translateX(-50%) scale(${gaugeScale})`, transformOrigin: 'top center' }}>
                <CarbGauge
                  current={currentRemaining}
                  max={dailyTarget}
                  proteinConsumed={proteinConsumed}
                  proteinTarget={proteinTarget}
                >
                  {(() => {
                    const carbOverAmount = currentRemaining - dailyTarget;
                    const carbBudget = dailyTarget - currentRemaining;
                    const carbRatio = dailyTarget > 0 ? currentRemaining / dailyTarget : 0;
                    const carbOverPct = dailyTarget > 0 ? Math.round((currentRemaining / dailyTarget) * 100) : 0;
                    const carbOver130 = carbRatio >= 1.3;
                    const carbStatusText = carbOver130
                      ? `⚠️ ${isJapan ? '超過' : 'Over'} (${carbOverPct}%)`
                      : `✅ ${isJapan ? '良好' : 'Good'} (${carbOverPct}%)`;
                    const carbStatusColor = carbOver130 ? '#ef4444' : '#22c55e';

                    const proteinRemaining = proteinTarget - proteinConsumed;
                    const proteinRatio = proteinTarget > 0 ? proteinConsumed / proteinTarget : 0;
                    const proteinColor = proteinRatio >= 1.0 ? '#10b981' : '#f472b6';
                    const proteinStatusText = proteinRatio >= 1.0
                      ? `✅ ${isJapan ? '良好' : 'Good'} (${Math.round(proteinRatio * 100)}%)`
                      : `⚠️ ${isJapan ? '不足' : 'Low'} (${Math.round(proteinRatio * 100)}%)`;

                    return (
                      <>
                        <div style={{ textAlign: 'center', lineHeight: 1.1, fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>
                          <div style={{ fontSize: '16px', color: '#9ca3af', fontWeight: '800', letterSpacing: '0.05em', marginBottom: '2px' }}>🍞 {isJapan ? '糖質' : 'Carbs'}</div>
                          <div style={{ fontSize: '52px', fontWeight: '900', color: remainingColor, lineHeight: 1 }}>
                            {fmt1(currentRemaining)}<span style={{ fontSize: '20px' }}>g</span>
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: '800', color: carbStatusColor, marginTop: '2px' }}>
                            {carbStatusText}
                          </div>
                        </div>
                        {proteinTarget > 0 && (
                          <div style={{ width: '60px', height: '1px', background: '#e5e7eb', margin: '6px 0' }} />
                        )}
                        {proteinTarget > 0 && (
                          <div style={{ textAlign: 'center', lineHeight: 1.1, fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>
                            <div style={{ fontSize: '16px', color: '#9ca3af', fontWeight: '800', letterSpacing: '0.05em', marginBottom: '2px' }}>💪 {isJapan ? 'タンパク質' : 'Protein'}</div>
                            <div style={{ fontSize: '52px', fontWeight: '900', color: proteinColor, lineHeight: 1 }}>
                              {fmt1(proteinConsumed)}<span style={{ fontSize: '20px' }}>g</span>
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: '800', color: proteinColor, marginTop: '2px' }}>
                              {proteinStatusText}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </CarbGauge>
              </div>
              </div>

              {/* ── 28日間平均（円グラフ下に常時表示） ── */}
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
                <div style={{ textAlign: 'center', fontSize: '16px', fontWeight: 'bold', color: '#854d0e', marginBottom: '12px' }}>
                  📊 {isJapan ? '28日間平均' : '28-Day Average'}
                </div>

                {isStatsLoading && (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '12px 0' }}>🔄 {isJapan ? '集計中...' : 'Loading...'}</div>
                )}

                {!isStatsLoading && statsData && !statsData.error && (
                  <>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                      <div style={{ flex: 1, textAlign: 'center', lineHeight: 1.1, fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>
                        <div style={{ fontSize: '16px', color: '#9ca3af', fontWeight: '800', letterSpacing: '0.05em', marginBottom: '4px' }}>🍞 {isJapan ? '糖質' : 'Carbs'}</div>
                        <div style={{ fontSize: '40px', fontWeight: '900', color: getColorByRatio(statsData.carbAvg, dailyTarget), lineHeight: 1 }}>
                          {fmt1(statsData.carbAvg)}<span style={{ fontSize: '16px' }}>g</span>
                        </div>
                      </div>
                      <div style={{ flex: 1, textAlign: 'center', lineHeight: 1.1, fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>
                        <div style={{ fontSize: '16px', color: '#9ca3af', fontWeight: '800', letterSpacing: '0.05em', marginBottom: '4px' }}>💪 {isJapan ? 'タンパク質' : 'Protein'}</div>
                        <div style={{ fontSize: '40px', fontWeight: '900', color: (proteinTarget > 0 && statsData.proteinAvg / proteinTarget >= 1.0) ? '#10b981' : '#f472b6', lineHeight: 1 }}>
                          {fmt1(statsData.proteinAvg)}<span style={{ fontSize: '16px' }}>g</span>
                        </div>
                      </div>
                    </div>

                    <div style={{ textAlign: 'center', fontSize: '16px', color: '#9ca3af', marginTop: '10px' }}>
                      {isJapan
                        ? `記録 ${statsData.recordCount}件 ／ ${new Date(statsData.since).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })} 〜 今日`
                        : `${statsData.recordCount} records ／ ${new Date(statsData.since).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – Today`}
                    </div>
                  </>
                )}

                {!isStatsLoading && statsData?.error && (
                  <div style={{ textAlign: 'center', color: '#ef4444', padding: '8px 0' }}>
                    {isJapan ? '取得に失敗しました' : 'Failed to load'}
                  </div>
                )}
              </div>
          </div>

          {/* 糖質・タンパク質ETA表示 */}
          {(() => {
            const MS_48H_ETA = 48 * 60 * 60 * 1000;
            const activeGramsSum = records.filter(r => r.addedGrams != null && (nowMs - r.timestamp) < MS_48H_ETA).reduce((s, r) => s + r.addedGrams, 0);
            const fmtEtaTime = (ms) => {
              const t = new Date(nowMs + ms);
              const hh = t.getHours(), mm = t.getMinutes();
              return isJapan
                ? `${hh}:${String(mm).padStart(2,'0')}`
                : `${hh % 12 || 12}:${String(mm).padStart(2,'0')} ${hh < 12 ? 'AM' : 'PM'}`;
            };
            const eta70Ms  = activeGramsSum > 0 && currentRemaining > dailyTarget * 0.85 ? (currentRemaining - dailyTarget * 0.85) * MS_48H_ETA / activeGramsSum : null;
            const carbRatio = dailyTarget > 0 ? currentRemaining / dailyTarget : 0;
            const canEatGrams = Math.round(dailyTarget * 1.3 - currentRemaining);

            const activeProteinGramsSum = records.filter(r => r.proteinGrams != null && (nowMs - r.timestamp) < MS_48H_ETA).reduce((s, r) => s + r.proteinGrams, 0);
            const etaProteinMs = activeProteinGramsSum > 0 && proteinTarget > 0 && proteinConsumed >= proteinTarget
              ? (proteinConsumed - proteinTarget) * MS_48H_ETA / activeProteinGramsSum
              : null;

            return (
              <>
                {eta70Ms && (
                  carbRatio > 1.45
                  ? <div style={{ backgroundColor: 'rgba(254,226,226,0.9)', borderRadius: '12px', padding: '10px 16px', border: '1px solid #fca5a5', textAlign: 'center', fontSize: '16px', color: '#b91c1c', fontWeight: 'bold' }}>
                      {isJapan ? `🍚 糖質制限推奨・${fmtEtaTime(eta70Ms)}頃解除予測` : `🍚 Carb restriction recommended — lifts ~${fmtEtaTime(eta70Ms)}`}
                    </div>
                  : carbRatio > 1.15
                  ? <div style={{ backgroundColor: 'rgba(254,243,199,0.9)', borderRadius: '12px', padding: '10px 16px', border: '1px solid #fcd34d', textAlign: 'center', fontSize: '16px', color: '#92400e', fontWeight: 'bold' }}>
                      {isJapan ? `🍚 糖質制限推奨・${fmtEtaTime(eta70Ms)}頃解除予測` : `🍚 Carb restriction recommended — lifts ~${fmtEtaTime(eta70Ms)}`}
                    </div>
                  : carbRatio > 0.85
                  ? <div style={{ backgroundColor: 'rgba(187,247,208,0.9)', borderRadius: '12px', padding: '10px 16px', border: '1px solid #4ade80', textAlign: 'center', fontSize: '16px', color: '#16a34a', fontWeight: 'bold' }}>
                      {isJapan ? `🍚 糖質制限推奨・${fmtEtaTime(eta70Ms)}頃解除予測` : `🍚 Carb restriction recommended — lifts ~${fmtEtaTime(eta70Ms)}`}
                    </div>
                  : <div style={{ backgroundColor: 'rgba(220,252,231,0.9)', borderRadius: '12px', padding: '10px 16px', border: '1px solid #86efac', textAlign: 'center', fontSize: '16px', color: '#166534', fontWeight: 'bold' }}>
                      {isJapan ? `🍚 糖質制限推奨・${fmtEtaTime(eta70Ms)}頃解除予測` : `🍚 Carb restriction recommended — lifts ~${fmtEtaTime(eta70Ms)}`}
                    </div>
                )}
                {carbRatio < 0.85 && (
                  <div style={{ backgroundColor: 'rgba(220,252,231,0.9)', borderRadius: '12px', padding: '10px 16px', border: '1px solid #86efac', textAlign: 'center', fontSize: '16px', color: '#166534', fontWeight: 'bold' }}>
                    {(() => {
                      const to115 = Math.round(dailyTarget * 1.15 - currentRemaining);
                      return isJapan
                        ? `🍞 あと${to115}g摂取可`
                        : `🍞 +${to115}g more OK`;
                    })()}
                  </div>
                )}
                {proteinTarget > 0 && proteinConsumed >= proteinTarget && (
                  <div style={{ backgroundColor: 'rgba(220,252,231,0.9)', borderRadius: '12px', padding: '10px 16px', border: '1px solid #86efac', textAlign: 'center', fontSize: '16px', color: '#166534', fontWeight: 'bold' }}>
                    {etaProteinMs
                      ? (isJapan
                          ? `💪 タンパク質良好・${fmtEtaTime(etaProteinMs)}頃補給推奨`
                          : `💪 Protein good — refill ~${fmtEtaTime(etaProteinMs)}`)
                      : (isJapan ? `💪 タンパク質良好` : `💪 Protein good`)}
                  </div>
                )}
              </>
            );
          })()}

          {/* タンパク質100%未満の補給推奨メッセージ（黄色） */}
          {proteinTarget > 0 && proteinConsumed < proteinTarget && (
            <div style={{ backgroundColor: 'rgba(254,243,199,0.9)', borderRadius: '12px', padding: '10px 16px', border: '1px solid #fcd34d', textAlign: 'center', fontSize: '16px', color: '#92400e', fontWeight: 'bold' }}>
              {isJapan
                ? `💪 補給${fmt1(proteinTarget * 1.15 - proteinConsumed)}g以上推奨`
                : `💪 +${fmt1(proteinTarget * 1.15 - proteinConsumed)}g protein recommended`}
            </div>
          )}

          {/* 食品入力フォーム */}
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '16px', border: `1px solid ${COLORS.cardBorder}`, boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowSettings(v => !v)}
                style={{ fontSize: '16px', padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '16px', backgroundColor: showSettings ? '#f3f4f6' : '#fff', color: COLORS.textSub, cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Icons.Settings /> {showSettings ? t('settings_close') : t('settings_label')}
              </button>
            </div>

            {/* 品目 + AIボタン */}
            <div>
              <label style={{ fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>🍽️ {t('food')}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} style={{ display: 'none' }} onChange={handleImageUpload} />
                <input type="file" accept="image/*" ref={galleryInputRef} style={{ display: 'none' }} onChange={handleImageUpload} />
                <input
                  type="text"
                  value={itemName}
                  onChange={e => handleItemNameChange(e.target.value)}
                  placeholder={t('item_sample')}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${COLORS.cardBorder}`, fontSize: '16px', backgroundColor: COLORS.inputBg }}
                />
                <button
                  type="button"
                  disabled={analyzingMode !== null || isAuthChecking}
                  onClick={handleAIScan}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', backgroundColor: analyzingMode ? '#dbeafe' : COLORS.accent, color: '#fff', fontWeight: 'bold', fontSize: '16px', cursor: analyzingMode ? 'wait' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {analyzingMode ? '🔍...' : <><Icons.Bot /> AI</>}
                </button>
              </div>
            </div>

            {/* 糖質・タンパク質 */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>🍞 {t('carbs_record_label')}</label>
                <div style={{ position: 'relative' }}>
                  <input type="number" className="no-spinner" value={carbs} onChange={e => setCarbs(e.target.value)} placeholder="0"
                    style={{ width: '100%', padding: '10px 32px 10px 10px', borderRadius: '8px', border: `1px solid ${COLORS.cardBorder}`, fontSize: '16px', boxSizing: 'border-box', backgroundColor: COLORS.inputBg }} />
                  <span style={{ position: 'absolute', right: '10px', top: '10px', fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold' }}>g</span>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>💪 {t('protein_record_label')}</label>
                <div style={{ position: 'relative' }}>
                  <input type="number" className="no-spinner" value={proteinInput} onChange={e => setProteinInput(e.target.value)} placeholder="0"
                    style={{ width: '100%', padding: '10px 32px 10px 10px', borderRadius: '8px', border: `1px solid ${COLORS.cardBorder}`, fontSize: '16px', boxSizing: 'border-box', backgroundColor: COLORS.inputBg }} />
                  <span style={{ position: 'absolute', right: '10px', top: '10px', fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold' }}>g</span>
                </div>
              </div>
            </div>

            {/* 設定パネル（折りたたみ） */}
            {showSettings && (
              <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ backgroundColor: '#f8f5ff', borderRadius: '12px', padding: '14px 16px', border: '1px solid #e9e0ff' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', color: '#7c3aed', fontWeight: 'bold' }}>📟 {t('height')} ({t('height_unit')})</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input type="number" className="no-spinner" value={heightInput} placeholder={t('height_placeholder')}
                      onChange={e => { const val = e.target.value; setHeightInput(val); setShowHeightSuggest(!!calcNutritionTargets(val)); }}
                      style={{ flex: 1, padding: '10px 14px', border: '1px solid #ddd6fe', borderRadius: '10px', fontSize: '16px', color: COLORS.textMain, outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box', backgroundColor: '#fff' }} />
                    <span style={{ fontSize: '16px', color: '#7c3aed', fontWeight: 'bold' }}>{t('height_unit')}</span>
                  </div>
                  {showHeightSuggest && (() => {
                    const s = calcNutritionTargets(heightInput);
                    if (!s) return null;
                    return (
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                          <span style={{ fontSize: '16px', backgroundColor: '#ede9fe', color: '#6d28d9', padding: '4px 10px', borderRadius: '20px' }}>{t('std_weight_label')}: <strong>{s.stdWeight}kg</strong></span>
                          <span style={{ fontSize: '16px', backgroundColor: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: '20px' }}>{t('suggest_carb')}: <strong>{s.carbTarget}g</strong></span>
                          <span style={{ fontSize: '16px', backgroundColor: '#fdf4ff', color: '#a21caf', padding: '4px 10px', borderRadius: '20px' }}>{t('suggest_protein')}: <strong>{s.proteinTarget}g</strong></span>
                        </div>
                        <button type="button" onClick={() => { setDailyDecayInput(s.carbTarget); setProteinTarget(s.proteinTarget); localStorage.setItem('protein_target', String(s.proteinTarget)); setShowHeightSuggest(false); }}
                          style={{ width: '100%', padding: '9px', fontSize: '16px', fontWeight: 'bold', backgroundColor: '#7c3aed', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer' }}>
                          ✅ {t('suggest_apply')}
                        </button>
                      </div>
                    );
                  })()}
                </div>
                <div style={{ backgroundColor: '#f0fdf4', borderRadius: '12px', padding: '14px 16px', border: '1px solid #bbf7d0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '16px', color: '#15803d', fontWeight: 'bold' }}>🥗 {t('daily_decay')} <DailyDecayHelpPopup /></label>
                    <span style={{ fontSize: '16px', color: '#15803d', backgroundColor: '#dcfce7', padding: '3px 10px', borderRadius: '20px' }}>{(dailyDecayInput/24).toFixed(1)}{t('rate_unit')}</span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input type="number" className="no-spinner" value={dailyDecayInput}
                      onChange={e => { const val = e.target.value; if (val === '' || /^[0-9]+$/.test(val)) setDailyDecayInput(val === '' ? '' : Number(val)); }}
                      style={{ width: '100%', padding: '12px 44px 12px 16px', backgroundColor: '#fff', border: '1px solid #bbf7d0', borderRadius: '10px', fontSize: '16px', color: COLORS.textMain, outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                    <div style={{ position: 'absolute', right: '14px', top: '12px', fontSize: '16px', color: '#15803d', fontWeight: 'bold' }}>g</div>
                  </div>
                </div>
                <div style={{ backgroundColor: '#fdf4ff', borderRadius: '12px', padding: '14px 16px', border: '1px solid #f5d0fe' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', color: '#a21caf', fontWeight: 'bold' }}>💪 {t('protein_target_setting')}</label>
                  <div style={{ position: 'relative' }}>
                    <input type="number" className="no-spinner" value={proteinTarget}
                      onChange={e => { const val = Number(e.target.value); if (!isNaN(val) && val >= 0) setProteinTarget(val); }}
                      style={{ width: '100%', padding: '12px 44px 12px 16px', backgroundColor: '#fff', border: '1px solid #f5d0fe', borderRadius: '10px', fontSize: '16px', color: COLORS.textMain, outline: 'none', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                    <div style={{ position: 'absolute', right: '14px', top: '12px', fontSize: '16px', color: '#a21caf', fontWeight: 'bold' }}>g</div>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '16px', color: '#a21caf', textAlign: 'right' }}>{(proteinTarget / 24).toFixed(1)} {t('rate_unit')}</div>
                </div>
              </div>
            )}
          </div>

          {/* たべた！ボタン */}
          <button
            onClick={handleAdd}
            disabled={isAuthChecking || isRecordsLoading || !itemName.trim()}
            style={{
              width: '100%', padding: '18px', fontSize: '20px', fontWeight: 'bold',
              backgroundColor: (isAuthChecking || isRecordsLoading || !itemName.trim()) ? '#9ca3af' : COLORS.accent,
              color: '#fff', borderRadius: '16px', border: 'none',
              cursor: (isAuthChecking || isRecordsLoading || !itemName.trim()) ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(37,99,235,0.3)', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
            }}
          >
            🍽️ {isRecordsLoading ? (isJapan ? '読み込み中...' : 'Loading...') : t('submit_button')}
          </button>

          {isRecordsLoading && (
            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '16px', border: `1px solid ${COLORS.cardBorder}`, textAlign: 'center', color: COLORS.textSub, fontSize: '16px' }}>
              {isJapan ? '🔄 履歴を読み込み中...' : '🔄 Loading history...'}
            </div>
          )}
          {!isRecordsLoading && records.length > 0 && (
            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '16px', border: `1px solid ${COLORS.cardBorder}`, boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.textMain, margin: 0 }}>🥖{t('memories')}🥐</h2>
                <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '16px', color: '#1d9bf0', backgroundColor: '#e0f2fe', borderRadius: '9999px', textDecoration: 'none', fontWeight: 'bold' }}>
                  <Icons.Twitter /> {t('share_button')}
                </a>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {records.map((r, i) => {
                  const isOpen = swipedRecordId === r.timestamp;
                  const REVEAL = 76;
                  return (
                    <li key={i} style={{ borderBottom: i !== records.length - 1 ? `1px solid ${COLORS.cardBorder}` : 'none', position: 'relative', overflow: 'hidden' }}>
                      {/* 再登録ボタン（スワイプで露出） */}
                      <button
                        onClick={() => handleReAdd(r)}
                        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: REVEAL + 'px', backgroundColor: '#22c55e', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', color: '#fff', fontSize: '12px', fontWeight: 'bold' }}
                      >
                        <span style={{ fontSize: '18px' }}>🔁</span>
                        {isJapan ? '再登録' : 'Re-add'}
                      </button>
                      {/* メイン行 */}
                      <div
                        onTouchStart={e => {
                          swipeTouchStartX.current = e.touches[0].clientX;
                          swipeTouchStartY.current = e.touches[0].clientY;
                        }}
                        onTouchEnd={e => {
                          if (swipeTouchStartX.current === null) return;
                          const dx = swipeTouchStartX.current - e.changedTouches[0].clientX;
                          const dy = Math.abs(e.changedTouches[0].clientY - swipeTouchStartY.current);
                          if (Math.abs(dx) > dy) {
                            if (dx > 30) { setSwipedRecordId(r.timestamp); e.preventDefault(); }
                            else if (dx < -15) { setSwipedRecordId(null); e.preventDefault(); }
                          }
                          swipeTouchStartX.current = null;
                          swipeTouchStartY.current = null;
                        }}
                        onClick={() => {
                          if (isOpen) { setSwipedRecordId(null); return; }
                          setSwipedRecordId(r.timestamp);
                        }}
                        style={{ padding: '12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', transform: isOpen ? `translateX(-${REVEAL}px)` : 'translateX(0)', transition: 'transform 0.2s ease', backgroundColor: '#fff', cursor: 'pointer' }}
                      >
                        <div style={{ fontSize: '16px', color: COLORS.textSub, flexShrink: 0 }}>{new Date(r.timestamp).toLocaleString(currentLocale, { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                        <div style={{ textAlign: 'right', flex: 1 }}>
                          <div style={{ fontSize: '16px', color: COLORS.textMain, fontWeight: '500' }}>{r.item || '---'}</div>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', marginTop: '2px' }}>
                            {r.addedGrams != null && r.addedGrams !== 0 && (
                              <span style={{ fontSize: '16px', color: COLORS.accent, fontWeight: 'bold' }}>🍞 {fmt1d(r.addedGrams)}g</span>
                            )}
                            {r.proteinGrams != null && r.proteinGrams !== 0 && (
                              <span style={{ fontSize: '16px', color: '#f472b6', fontWeight: 'bold' }}>💪 {fmt1d(r.proteinGrams)}g</span>
                            )}
                          </div>
                        </div>
                        <button onClick={e => { e.stopPropagation(); if (isOpen) { setSwipedRecordId(null); return; } openEditModal(r); }} style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }} onMouseEnter={e => e.currentTarget.style.color='#2563eb'} onMouseLeave={e => e.currentTarget.style.color='#9ca3af'}>
                          <Icons.Edit />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* 広告カード */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {isJapan ? (<AdMaxBanner_middle />) : (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}><OverseasBanner /><OverseasExtraAd /></div>)}
          </div>
        </div>



        {/* ── 占いモーダル ── */}
        {showFortunePopup && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
               onClick={e => { if (e.target === e.currentTarget) setShowFortunePopup(false); }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '16px 20px', width: '100%', maxWidth: '480px', boxShadow: '0 -4px 20px rgba(0,0,0,0.2)', boxSizing: 'border-box', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                <button onClick={() => setShowFortunePopup(false)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', color: '#9ca3af', cursor: 'pointer', padding: '4px', lineHeight: 1 }}>
                  ✕
                </button>
              </div>
              <FortuneDiagnosis
                 fortune={fortuneData.fortune}
                 currentBirthDate={fortuneData.birthDate}
                 onSave={(data) => fetchFortune(data)}
                 t={t}
                 isJapan={isJapan}
                 isLoading={isFortuneLoading}
              />
            </div>
          </div>
        )}

        {/* ── 編集モーダル ── */}
        {editingRecord && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
               onClick={e => { if (e.target === e.currentTarget) setEditingRecord(null); }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '24px', width: '100%', maxWidth: '480px', boxShadow: '0 -4px 20px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: '14px', paddingBottom: 'calc(24px + env(safe-area-inset-bottom))' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.textMain, textAlign: 'center', marginBottom: '2px' }}>
                ✏️ {isJapan ? 'ログを編集' : 'Edit Log'}
              </div>

              {/* 日時 */}
              <div>
                <label style={{ fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                  {isJapan ? '日時' : 'Date & Time'}
                </label>
                <input
                  type="datetime-local"
                  value={editDatetime}
                  onChange={e => setEditDatetime(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${COLORS.cardBorder}`, fontSize: '16px', boxSizing: 'border-box', backgroundColor: COLORS.inputBg }}
                />
              </div>

              {/* 品目 + AIボタン */}
              <div>
                <label style={{ fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                  {isJapan ? '品目' : 'Food Item'}
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={editItem}
                    onChange={e => setEditItem(e.target.value)}
                    placeholder={isJapan ? '例: ごはん 200g' : 'e.g. rice 200g'}
                    style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${COLORS.cardBorder}`, fontSize: '16px', backgroundColor: COLORS.inputBg }}
                  />
                  <button
                    type="button"
                    onClick={handleEditAIScan}
                    disabled={!editItem.trim() || !!editAnalyzingMode}
                    style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', backgroundColor: editItem.trim() && !editAnalyzingMode ? '#2563eb' : '#e5e7eb', color: editItem.trim() && !editAnalyzingMode ? '#fff' : '#9ca3af', fontWeight: 'bold', fontSize: '16px', cursor: editItem.trim() && !editAnalyzingMode ? 'pointer' : 'default', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {editAnalyzingMode ? '🔍...' : '🤖 AI'}
                  </button>
                </div>
              </div>

              {/* 糖質・タンパク質 */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                    🍞 {isJapan ? '糖質 (g)' : 'Carbs (g)'}
                  </label>
                  <input
                    type="number"
                    value={editCarbs}
                    onChange={e => setEditCarbs(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${COLORS.cardBorder}`, fontSize: '16px', boxSizing: 'border-box', backgroundColor: COLORS.inputBg }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '16px', color: COLORS.textSub, fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                    💪 {isJapan ? 'タンパク質 (g)' : 'Protein (g)'}
                  </label>
                  <input
                    type="number"
                    value={editProtein}
                    onChange={e => setEditProtein(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${COLORS.cardBorder}`, fontSize: '16px', boxSizing: 'border-box', backgroundColor: COLORS.inputBg }}
                  />
                </div>
              </div>

              {/* 保存・キャンセル・削除 */}
              <button
                onClick={handleEditSave}
                style={{ padding: '14px', borderRadius: '10px', border: 'none', backgroundColor: '#22c55e', color: '#fff', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' }}
              >
                {isJapan ? '💾 保存' : '💾 Save'}
              </button>
              <button
                onClick={() => setEditingRecord(null)}
                style={{ padding: '12px', borderRadius: '10px', border: `1px solid ${COLORS.cardBorder}`, backgroundColor: '#fff', color: COLORS.textSub, fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' }}
              >
                {isJapan ? 'キャンセル' : 'Cancel'}
              </button>
              <button
                onClick={handleEditDelete}
                style={{ padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#fee2e2', color: '#dc2626', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' }}
              >
                🗑️ {isJapan ? 'このログを削除' : 'Delete this log'}
              </button>

              {/* 広告カード */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
                {isJapan ? (<AdMaxBanner_middle />) : (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}><OverseasBanner /><OverseasExtraAd /></div>)}
              </div>
            </div>
          </div>
        )}

        {/* ── PWAインストール促進バナー ── */}
        {showInstallBanner && (() => {
          const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
          const ua = navigator.userAgent;
          const iosBrowser = isIos
            ? /crios/i.test(ua) ? 'chrome'
            : /fxios/i.test(ua) ? 'firefox'
            : /edgios/i.test(ua) ? 'edge'
            : 'safari'
            : null;
          const handleDismiss = () => {
            setShowInstallBanner(false);
            localStorage.setItem('installBannerDismissed', String(Date.now()));
          };
          const handleInstall = async () => {
            if (deferredInstallPrompt) {
              deferredInstallPrompt.prompt();
              const { outcome } = await deferredInstallPrompt.userChoice;
              if (outcome === 'accepted') {
                setShowInstallBanner(false);
                setDeferredInstallPrompt(null);
              }
            }
          };
          return (
            <div style={{
              position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
              backgroundColor: '#1e293b', color: '#fff',
              padding: '14px 16px calc(14px + env(safe-area-inset-bottom)) 16px',
              boxShadow: '0 -4px 16px rgba(0,0,0,0.3)',
              display: 'flex', flexDirection: 'column', gap: '10px',
              maxWidth: '480px', margin: '0 auto',
              borderRadius: '16px 16px 0 0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <img src="/icons/icon-192x192.png" alt="icon" style={{ width: '40px', height: '40px', borderRadius: '10px' }} />
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {isJapan ? 'ホーム画面に追加' : 'Add to Home Screen'}
                    </div>
                    <div style={{ fontSize: '16px', color: '#94a3b8', marginTop: '2px' }}>
                      {isJapan ? 'アプリとして快適に使えます' : 'Use as a native-feel app'}
                    </div>
                  </div>
                </div>
                <button onClick={handleDismiss} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '20px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
              </div>

              {isIos ? (
                // iOSは手順案内のみ（ブラウザ別）
                <div style={{ backgroundColor: '#334155', borderRadius: '10px', padding: '10px 12px', fontSize: '16px', color: '#cbd5e1', lineHeight: 1.7 }}>
                  {iosBrowser === 'safari' && (isJapan ? (
                    <>① Safariの <span style={{ fontSize: '16px' }}>⬆</span> 共有ボタンをタップ<br />② 「ホーム画面に追加」を選択</>
                  ) : (
                    <>① Tap the <span style={{ fontSize: '16px' }}>⬆</span> Share button in Safari<br />② Select "Add to Home Screen"</>
                  ))}
                  {iosBrowser === 'chrome' && (isJapan ? (
                    <>① 画面下の <span style={{ fontSize: '16px' }}>⬆</span> 共有ボタンをタップ<br />② 「ホーム画面に追加」を選択</>
                  ) : (
                    <>① Tap the <span style={{ fontSize: '16px' }}>⬆</span> Share button at the bottom<br />② Select "Add to Home Screen"</>
                  ))}
                  {iosBrowser === 'firefox' && (isJapan ? (
                    <>① アドレスバー右の <span style={{ fontSize: '16px' }}>⋯</span> メニューをタップ<br />② 「ページをショートカットに追加」を選択</>
                  ) : (
                    <>① Tap the <span style={{ fontSize: '16px' }}>⋯</span> menu next to the address bar<br />② Select "Add to Shortcuts"</>
                  ))}
                  {iosBrowser === 'edge' && (isJapan ? (
                    <>① 画面下の <span style={{ fontSize: '16px' }}>⋯</span> メニューをタップ<br />② 「電話に追加」→「ホーム画面に追加」を選択</>
                  ) : (
                    <>① Tap the <span style={{ fontSize: '16px' }}>⋯</span> menu at the bottom<br />② Select "Add to Phone" → "Add to Home Screen"</>
                  ))}
                </div>
              ) : (
                // Android: ネイティブダイアログを呼び出すボタン
                <button onClick={handleInstall} style={{
                  padding: '11px', borderRadius: '10px', border: 'none',
                  backgroundColor: '#22c55e', color: '#fff',
                  fontWeight: 'bold', fontSize: '16px', cursor: 'pointer',
                }}>
                  {isJapan ? '📲 ホーム画面に追加する' : '📲 Add to Home Screen'}
                </button>
              )}
            </div>
          );
        })()}

        <footer style={{ padding: '24px', textAlign: 'center', fontSize: '16px', color: COLORS.textSub, borderTop: `1px solid ${COLORS.cardBorder}`, marginTop: '20px', fontWeight: 'bold' }}>
          &copy; 2025 <a href="https://sugar.michitaru.com" style={{ color: COLORS.textMain, textDecoration: 'none' }}>{t('title')}</a>. All rights reserved.<br />
          <p style={{ marginTop: '8px' }}>{t('contact_label')}: <a href="https://x.com/Mr_Carbohydra" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>X (formerly Twitter) @Mr_Carbohydra</a></p>
          <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <a href={t('link.privacy')} style={{ color: COLORS.textSub }}>{t('policy')}</a>
            <a href={t('link.about')} style={{ color: COLORS.textSub }}>{t('site_info')}</a>
          </div>
        </footer>
      </div>
      
      {showSourceMenu && (
        <div style={menuStyles.overlay} onClick={() => setShowSourceMenu(false)}>
          <div style={menuStyles.container} onClick={(e) => e.stopPropagation()}>
            <div style={menuStyles.title}>{t('upload_method')}</div>
            
            <button 
              style={menuStyles.button}
              onClick={() => { cameraInputRef.current.click(); }}
            >
              <Icons.Camera /> {t('take_photo')}
            </button>
            
            <button 
              style={menuStyles.button}
              onClick={() => { galleryInputRef.current.click(); }}
            >
              <span style={{ fontSize: '18px' }}>🖼️</span> {t('choose_gallery')}
            </button>
            
            <button 
              style={menuStyles.cancelButton}
              onClick={() => setShowSourceMenu(false)}
            >
              {t('cancel_selection')}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}